import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import 'ol/ol.css';
import { Map, View } from 'ol';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { Draw, Modify, Select, Snap } from 'ol/interaction';
import { click } from 'ol/events/condition';
import { Vector as VectorSource, XYZ, Raster as RasterSource } from 'ol/source';
import { Vector as VectorLayer, Tile as TileLayer, Image as ImageLayer } from 'ol/layer';
import type BaseLayer from 'ol/layer/Base';
import { Style, Fill, Stroke, Circle } from 'ol/style';
import { GeoJSON } from 'ol/format';
import { Feature } from 'ol';
import { Point, Polygon as OlPolygon } from 'ol/geom';

// Import components
import MapControls from '../utils/MapControls';
// `SearchBar` (place / coordinate search) removed from the map — the top-right header
// search in `HomePage` is the single search entry point now.
import TimeSeriesSlider from '../utils/TimeSeriesSlider';
import MapChangeSwipe from '../utils/ImageComparisonSlider';
import MapLegend from '../utils/MapLegend';
import MapStatusIndicators from '../utils/MapStatusIndicators';
import WMSTimeSeriesSlider from './WMSTimeSeriesSlider';
import HeatMapObservationBar, {
  type HeatMapObservationBarProps,
} from './HeatMapObservationBar';
import FarmIndexWalkOverlay, {
  type FarmWalkReport,
} from './FarmIndexWalkOverlay';
import { useTheme } from '../theme/ThemeContext';
import { getMapLegendTheme } from '../theme/themeStyles';
// `LayerTogglePanel` is no longer rendered from inside MapView (the hamburger gate was replaced
// by HomePage's `MapToolRail`). Kept import path documented here in case the inline panel returns.

// Import enhanced layer manager
import { MapLayerManager } from '../utils/MapLayerManager';
import { defaultBsriDateRange } from '../utils/bsriDateRange';
import type { CropAreaLayerKey, LayerVisibilityState, LayerOpacityState } from '../utils/MapLayerManager';
import InfoPopupDialog from './InfoPopupDialog';
import { CADI_DROUGHT_FRAMES, CADI_DROUGHT_DESCRIPTION, CADI_DROUGHT_LEGEND_DESCRIPTION, CADI_FILL_NODATA, defaultCadiFrameIndex, fetchCADIPointValue } from '../constants/cadiDroughtLayers';
import {
  DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID,
  RICE_GROWTH_STAGE_LEGEND,
  fetchRiceGrowthStagePoint,
  getRiceGrowthStagePeriod,
  type RiceGrowthStagePeriodId,
} from '../constants/riceGrowthStageLayers';
import { fetchIrrigationTypePoint } from '../constants/irrigationTypeLayers';
import {
  PLANTING_METHOD_COGS,
  PLANTING_METHOD_LEGEND,
  fetchPlantingMethodPoint,
} from '../constants/plantingMethodLegend';
import { IRRIGATION_TYPE_LABEL, IRRIGATION_TYPE_LEGEND } from '../constants/irrigationTypeLayers';
import { COCONUT_CROP_BBOX } from '../constants/coconutCropLayers';
import { MAIZE_CROP_BBOX } from '../constants/maizeCropLayers';
import { RICE_CROP_BBOX } from '../constants/riceCropLayers';
import { SUGARCANE_CROP_BBOX } from '../constants/sugarcaneCropLayers';
import { FLOOD_DAMAGE_RASTER_LEGEND } from '../utils/floodDamagePlaces';
import {
  DEFAULT_PEST_RISK_PEST_KEY,
  fetchPestPointValue,
  getPestRiskFrames,
  getPestRiskPestLabel,
  PEST_RISK_DESCRIPTION,
  PEST_RISK_LEGEND_DESCRIPTION,
  type PestRiskPestKey,
} from '../constants/pestRiskLayers';
import type { DronePrescriptionState } from './DronePrescriptionPanel';
import {
  type DroneMissionConfig,
  resolveAnalysisGeometry,
} from '../constants/dronePrescriptionLayers';
import { findDroneLayer } from '../constants/droneSites';
import { fetchDronePrescriptionAnalytics } from '../utils/dronePrescriptionAnalytics';
import {
  fetchMunicipalitiesByProvince,
  fetchMunicipalityGeometry,
  fetchProvinceBbox,
  fetchProvinceMunicipalityZonalStats,
  type MultitemporalZonalLayer,
  type MultitemporalZonalPanelState,
} from '../utils/multitemporalZonalAnalytics';

// Import hooks
import { useMapControls } from '../utils/hooks/useMapControls';
import { useTimeSeriesControl } from '../utils/hooks/useTimeSeriesControl';
import { useLayerControls } from '../utils/hooks/useLayerControls';
import {
  DISPLAY_SCALE_CHANGE_EVENT,
  getMapPixelRatio,
} from '../utils/mapDisplayScale';
import type { WMSLayerState } from '../utils/hooks/useWMSLayers';

// Import utilities
import {
  getCurrentAnalysisInfo,
  getVisualizationParams
} from '../utils/utils/MapUtils';
import { normalizeDateRange } from '../utils/dateRangeInputs';
import { FARM_WEBGIS_API_BASE_URL, type StrawBurnFetchPayload } from '../../../config/farmWebgisApi';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { digisakaAuthRequestInit, gptInferenceJsonHeaders } from '../../../config/digisakaGptInference';
import { buildAllowedEntitlementSet } from '../../../config/analysisLayerEntitlements';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';

interface MapViewProps {
  selectedField?: any;
  fieldGeoJson?: any;
  /** Opened farm group FeatureCollection — polygons + name labels, fit to extent */
  farmGroupGeoJson?: any | null;
  /** When false, draw farm overview without auto-zooming (default true for groups). */
  farmGroupFit?: boolean;
  /** LGU assigned monitoring area boundary (GeoJSON Feature). */
  monitoringAreaBoundaryGeoJson?: any | null;
  analysisResults?: any;
  indexHeatMapData?: any;
  timeSeriesData?: any;
  onFieldClick?: (field: any) => void;
  /** Click a detected AI delineation polygon while in picking mode. */
  onDelineationFieldClick?: (
    featureIndex: number,
    modifiers?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }
  ) => void;
  /** Click empty map while picking delineation blocks — clear selection. */
  onDelineationClearSelection?: () => void;
  /** When true, map clicks prioritize delineation polygon selection. */
  isAiDelineationPicking?: boolean;
  /** Right-click on the farm boundary polygon (e.g. crop calendar pre-input). */
  onFieldContextMenu?: (fieldProperties: Record<string, unknown>) => void;
  onZoomToField?: (fieldId: any) => void;
  onTimeSeriesSliderChange?: (imageData: any) => void;
  isLoadingGeoJson?: boolean;
  className?: string;
  
  // Drawing props
  isDrawingMode?: boolean;
  /** When true, at least one block is closed — Modify/Select enabled; Draw stays on for more blocks. */
  manualDrawEditing?: boolean;
  onFieldDrawn?: (geoJsonFeature: any) => void;
  /** Fired when the user closes the polygon; parent should enter vertex-edit before confirming. */
  onDrawPolygonReady?: (geoJsonFeature: any) => void;
  
  // WMS props
  wmsLayerStates?: WMSLayerState;
  wmsDateArrays?: {
    yield: string[];
    vhi: string[];
    lst: string[];
  };
  onWMSDateChange?: (date: string, layerType: 'yield' | 'vhi' | 'lst') => void;
  onWMSLayerToggle?: (layerType: 'yield' | 'vhi' | 'lst') => void;
  onWMSPlayToggle?: (layerType: 'yield' | 'vhi' | 'lst') => void;
  onSwitchToLargeScaleMode?: () => void;
  /** From GET /auth/me — when set, map tools match `analysis_layers` / role entitlements */
  allowedLayerKeys?: string[];
  /** Drone NDVI prescription stats — surfaced in the left AnalysisPanel. */
  onDronePrescriptionUpdate?: (payload: {
    state: DronePrescriptionState;
    mission: DroneMissionConfig;
    usingMissionExtent: boolean;
  } | null) => void;
  /** CADI / pest risk municipality zonal stats — left sidebar panel. */
  onMultitemporalZonalUpdate?: (payload: {
    state: MultitemporalZonalPanelState;
    layer: MultitemporalZonalLayer;
    frameLabel: string;
    pestLabel?: string;
  } | null) => void;
  /** Bottom observation-date card bar for farm heat map (UI-first). */
  heatMapObservationBar?: HeatMapObservationBarProps | null;
  /** End-of-walk report → Pandoy AI (Index Map walk mode). */
  onFarmWalkReport?: (report: FarmWalkReport) => void;
  onLayerControlReady?: (controls: {
    getCurrentLayerVisibility: () => LayerVisibilityState;
    layerVisibility: LayerVisibilityState; // Add direct access to state for immediate updates
    layerOpacity: LayerOpacityState;
    handleLayerToggle: (layerType: keyof LayerVisibilityState, visible: boolean) => void;
    handleOpacityChange: (layerType: keyof LayerOpacityState, opacity: number) => void;
    /** Registry drone sites (`droneSites.ts`) — active layer id, opacities and handlers. */
    droneSiteActiveLayerId: string | null;
    droneSiteOpacity: Record<string, number>;
    handleDroneSiteLayerToggle: (layerId: string, visible: boolean) => void;
    handleDroneSiteOpacityChange: (layerId: string, opacity: number) => void;
    clearDroneSiteLayers: () => void;
    getLegendUrls: () => Record<string, string>;
    getCropAreaLegendInfo: () => Record<string, { colors: string[]; labels: string[]; description: string; totalArea?: number }>;
    cropAreaLoadingLayers: Set<string>;
    handleForecastDataUpdate: (forecastData: any) => void;
    handleFloodLayerShow: (layerInfo: { wmsUrl: string; layerName: string; bbox: [number, number, number, number] }) => void;
    showFloodDamageRaster: (raster: import('../../../types/floodDamage').FloodDamageRasterRef | null, options?: { fit?: boolean; showLayer?: boolean }) => void;
    handleBSRIDateRangeUpdate: (dateRange: { start_date: string; end_date: string }) => void;
    bsriIsLoading: boolean;
    handleLSTPHDateRangeUpdate: (dateRange: { start_date: string; end_date: string }) => void;
    lstPHIsLoading: boolean;
    handleVHIPHDateRangeUpdate: (dateRange: { start_date: string; end_date: string }) => void;
    vhiPHIsLoading: boolean;
    clearMultiTemporalAPITiles: () => void;
    handleStrawBurnDataFetch: (payload: StrawBurnFetchPayload) => void;
    strawBurnIsLoading: boolean;
    strawBurnData: any;
    handleTreeDetectionDataUpdate: (data: any) => void;
    handleCropfieldDelineationUpdate: (data: any) => void;
    /** Crown segmentation polygons (geojson / geojson_wgs84 from API) */
    handleCrownSegmentationMapUpdate: (data: any) => void;
    /** Field Tools — AI delineation: highlight a polygon before editing */
    setCropfieldDelineationSelectedIndex: (index: number | null) => void;
    setCropfieldDelineationCheckedIndexes: (indexes: number[]) => void;
    setCropfieldDelineationMultiSelectMode: (active: boolean) => void;
    startCropfieldVertexEdit: (featureIndex: number) => boolean;
    cancelCropfieldVertexEdit: () => void;
    undoCropfieldVertexEdit: () => boolean;
    redoCropfieldVertexEdit: () => boolean;
    canUndoCropfieldVertexEdit: () => boolean;
    canRedoCropfieldVertexEdit: () => boolean;
    setCropfieldVertexHistoryListener: (
      listener: ((state: { canUndo: boolean; canRedo: boolean }) => void) | null
    ) => void;
    confirmCropfieldVertexEdit: () => { geojson: Record<string, unknown>; remainingCount: number } | null;
    /** Apply vertex edits in place (Split review) without removing the polygon. */
    applyCropfieldVertexEditInPlace: () => {
      featureIndex: number;
      geojson: Record<string, unknown>;
      featureCount: number;
    } | null;
    confirmCropfieldDelineationFeature: (
      featureIndex: number
    ) => { geojson: Record<string, unknown>; remainingCount: number } | null;
    confirmCropfieldDelineationFeatures: (
      featureIndices: number[]
    ) => { geojson: Record<string, unknown>; remainingCount: number } | null;
    getCropfieldDelineationCount: () => number;
    getCropfieldDelineationFeatureCollectionGeoJson: () => {
      type: 'FeatureCollection';
      features: Record<string, unknown>[];
    } | null;
    removeCropfieldDelineationFeatureAt: (featureIndex: number) => number;
    removeCropfieldDelineationFeaturesAt: (featureIndices: number[]) => number;
    isCropfieldVertexEditActive: () => boolean;
    hasManualDrawPolygon: () => boolean;
    getManualDrawBlockCount: () => number;
    getManualDrawGeoJson: () => Record<string, unknown> | null;
    clearManualDraw: () => void;
    restartManualDraw: () => void;
    removeSelectedManualDrawBlock: () => boolean;
    loadGeometryForEdit: (geometry: any) => void;
    flyToLocation: (lonLat: [number, number], zoom?: number) => void;
    /** Drop a search marker, pan + zoom the map to the chosen place/coordinate. Used by the
     *  top-right Nominatim search in `HomePage` (Philippines bias + reverse geocoding). */
    selectSearchLocation: (lat: number, lon: number, name: string) => void;
    mapCoordinates: [number, number];
    zoomLevel: number;
    /** OpenLayers map instance for viewport GeoTIFF + field delineation */
    getOlMap: () => Map | null;
    requestDronePrescriptionStats: (mission: DroneMissionConfig) => void;
    clearDronePrescriptionStats: () => void;
    /** Show orthophoto-computed index colormap (NDVI/NDRE/ExG/SAVI) on the map. */
    showDroneOrthoIndexMap: (
      mission: DroneMissionConfig,
      tileUrl: string
    ) => void;
    clearDroneOrthoIndexMap: () => void;
    requestMultitemporalProvinceStats: (province: string) => void;
    selectMultitemporalMunicipality: (municipality: string | null) => void;
    clearMultitemporalZonalStats: () => void;
    riceGrowthStageLoading: boolean;
    riceGrowthStagePeriod: RiceGrowthStagePeriodId;
    handleRiceGrowthStagePeriodChange: (id: RiceGrowthStagePeriodId) => void;
  }) => void;
  /** Finished drone flights (orthomosaic or DSM), drawn on the main map. */
  droneJobOverlay?: {
    /** Bottom to top. `key` changes only when the layer's source changes. */
    layers: { key: string; tileUrl: string; elevationUrl?: string }[];
    shots: { type: string; features: object[] } | null;
    /** The map fits to `extent` once per new `key`. */
    focus: { key: string; extent: [number, number, number, number] } | null;
  } | null;
}

/** Keep DTM color where the NDWI flood mask is opaque. Dry pixels stay empty. */
function floodGroundPixels(pixels: number[][]): number[] {
  const mask = pixels[0];
  const ground = pixels[1];
  if (!mask || !ground || mask[3] < 20) return [0, 0, 0, 0];
  return ground;
}

export default function MapView({
  selectedField,
  fieldGeoJson,
  farmGroupGeoJson = null,
  farmGroupFit = true,
  monitoringAreaBoundaryGeoJson = null,
  analysisResults,
  indexHeatMapData,
  timeSeriesData,
  onFieldClick,
  onDelineationFieldClick,
  onDelineationClearSelection,
  isAiDelineationPicking = false,
  onFieldContextMenu,
  onZoomToField,
  onTimeSeriesSliderChange,
  isLoadingGeoJson = false,
  className = "",
  
  // Drawing props
  isDrawingMode = false,
  manualDrawEditing = false,
  onFieldDrawn,
  onDrawPolygonReady,
  
  // WMS props
  wmsLayerStates,
  wmsDateArrays,
  onWMSDateChange,
  onWMSLayerToggle,
  onWMSPlayToggle,
  onSwitchToLargeScaleMode,
  allowedLayerKeys,
  onDronePrescriptionUpdate,
  onMultitemporalZonalUpdate,
  heatMapObservationBar = null,
  onFarmWalkReport,
  onLayerControlReady
  ,
  /** Finished drone orthomosaic or DSM, drawn on top of the basemap. */
  droneJobOverlay = null,
}: MapViewProps) {
  const { isDark } = useTheme();
  const lg = useMemo(() => getMapLegendTheme(isDark), [isDark]);
  const allowedEntitlementSet = useMemo(
    () => buildAllowedEntitlementSet(allowedLayerKeys),
    [allowedLayerKeys]
  );

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const layerManagerRef = useRef<MapLayerManager | null>(null);
  const onDelineationFieldClickRef = useRef(onDelineationFieldClick);
  const onDelineationClearSelectionRef = useRef(onDelineationClearSelection);
  const onFieldClickRef = useRef(onFieldClick);
  const isAiDelineationPickingRef = useRef(isAiDelineationPicking);
  const onFieldContextMenuRef = useRef(onFieldContextMenu);
  useEffect(() => {
    onDelineationFieldClickRef.current = onDelineationFieldClick;
  }, [onDelineationFieldClick]);
  useEffect(() => {
    onDelineationClearSelectionRef.current = onDelineationClearSelection;
  }, [onDelineationClearSelection]);
  useEffect(() => {
    onFieldClickRef.current = onFieldClick;
  }, [onFieldClick]);
  useEffect(() => {
    isAiDelineationPickingRef.current = isAiDelineationPicking;
  }, [isAiDelineationPicking]);
  const isDrawingModeRef = useRef(isDrawingMode);
  const manualDrawEditingRef = useRef(manualDrawEditing);
  const prevDrawingModeRef = useRef(isDrawingMode);
  useEffect(() => {
    onFieldContextMenuRef.current = onFieldContextMenu;
  }, [onFieldContextMenu]);
  useEffect(() => {
    isDrawingModeRef.current = isDrawingMode;
  }, [isDrawingMode]);
  useEffect(() => {
    manualDrawEditingRef.current = manualDrawEditing;
  }, [manualDrawEditing]);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  
  // Drawing-related refs
  const drawInteractionRef = useRef<Draw | null>(null);
  const modifyInteractionRef = useRef<Modify | null>(null);
  const selectInteractionRef = useRef<Select | null>(null);
  const snapInteractionRef = useRef<Snap | null>(null);
  const drawSourceRef = useRef<VectorSource | null>(null);
  const drawLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  
  // Search marker refs
  const searchMarkerSourceRef = useRef<VectorSource | null>(null);
  const searchMarkerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  // Change Detection States
  const [isChangeDetectionMode, setIsChangeDetectionMode] = useState(false);
  const [changeDetectionImages, setChangeDetectionImages] = useState<{
    before: any | null;
    after: any | null;
  }>({ before: null, after: null });
  const [showChangeDetectionArrow, setShowChangeDetectionArrow] = useState(false);

  const isWMSLayerSwitching = useRef(false);
  const wmsLayerSwitchCounter = useRef(0);

  // Non-WMS Layer states (heatMap and timeSeries only)
  const [layerVisibility, setLayerVisibility] = useState<LayerVisibilityState>({
    yield: false,
    vhi: false,
    lst: false,
    heatMap: false,
    timeSeries: false,
    maize: false,
    sugarCane: false,
    rice: false,
    coconut: false,
    droneNDVI: false,
    riceForecast: false,
    bsri: false,
    strawBurn: false,
    plantingMethodDry2025: false,
    plantingMethodDry2024: false,
    riceGrowthStage: false,
    irrigationType: false,
    cadiDrought: false,
    pestRisk: false,
    isabelaPhilriceNDVI: false,
    isabelaPhilriceOrtho: false,
    caviteSilangNDVI: false,
    caviteSilangOrtho: false,
    treeDetection: false,
  });

  // Use a ref to store the latest layerVisibility state for immediate access
  // This ensures getCurrentLayerVisibility always returns the latest state, even before React re-renders
  const layerVisibilityRef = useRef<LayerVisibilityState>(layerVisibility);
  
  // Keep ref in sync with state
  useEffect(() => {
    layerVisibilityRef.current = layerVisibility;
  }, [layerVisibility]);

  // Registry drone sites (`droneSites.ts`): one active layer id + per-layer opacity. Kept outside
  // `layerVisibility` because these ids are data-driven. MapLayerManager reports removals through
  // its listener, so any code path that clears drone layers keeps this state in sync.
  const [droneSiteActiveLayerId, setDroneSiteActiveLayerId] = useState<string | null>(null);
  const [droneSiteOpacity, setDroneSiteOpacity] = useState<Record<string, number>>({});


  const [layerOpacity, setLayerOpacity] = useState<LayerOpacityState>({
    yield: 1,
    vhi: 1,
    lst: 1,
    heatMap: 1,
    timeSeries: 1,
    maize: 1,
    sugarCane: 1,
    rice: 1,
    coconut: 1,
    droneNDVI: 1,
    riceForecast: 1,
    bsri: 1,
    strawBurn: 1,
    plantingMethodDry2025: 1,
    plantingMethodDry2024: 1,
    riceGrowthStage: 1,
    irrigationType: 1,
    cadiDrought: 1,
    pestRisk: 1,
    isabelaPhilriceNDVI: 1,
    isabelaPhilriceOrtho: 1,
    caviteSilangNDVI: 1,
    caviteSilangOrtho: 1,
    treeDetection: 1,
  });

  // Crop area loading states
  const [cropAreaLoadingLayers, setCropAreaLoadingLayers] = useState<Set<string>>(new Set());
  const [riceGrowthStageLoading, setRiceGrowthStageLoading] = useState(false);
  const [riceGrowthStagePeriod, setRiceGrowthStagePeriod] = useState<RiceGrowthStagePeriodId>(
    DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID
  );
  const riceGrowthStagePeriodRef = useRef<RiceGrowthStagePeriodId>(DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID);
  riceGrowthStagePeriodRef.current = riceGrowthStagePeriod;
  const [irrigationTypeLoading, setIrrigationTypeLoading] = useState(false);

  // Rice forecast data state
  const [riceForecastData, setRiceForecastData] = useState<any>(null);

  // Tree detection data state
  const [treeDetectionData, setTreeDetectionData] = useState<any>(null);

  // Black Sigatoka Risk Index data state
  const [bsriData, setBsriData] = useState<any>(null);
  const [bsriCurrentIndex, setBsriCurrentIndex] = useState(0);
  const [bsriIsPlaying, setBsriIsPlaying] = useState(false);
  const [bsriPlaybackSpeed, setBsriPlaybackSpeed] = useState(2000);
  const [bsriDateRange, setBsriDateRange] = useState(defaultBsriDateRange);
  const [bsriIsLoading, setBsriIsLoading] = useState(false);
  const bsriIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // LST Philippines (MODIS) data state
  const [lstPHData, setLstPHData] = useState<any>(null);
  const [lstPHCurrentIndex, setLstPHCurrentIndex] = useState(0);
  const [lstPHIsPlaying, setLstPHIsPlaying] = useState(false);
  const [lstPHPlaybackSpeed, setLstPHPlaybackSpeed] = useState(2000);
  const [lstPHIsLoading, setLstPHIsLoading] = useState(false);

  // VHI Philippines (agricultural drought) data state
  const [vhiPHData, setVhiPHData] = useState<any>(null);
  const [vhiPHCurrentIndex, setVhiPHCurrentIndex] = useState(0);
  const [vhiPHIsPlaying, setVhiPHIsPlaying] = useState(false);
  const [vhiPHPlaybackSpeed, setVhiPHPlaybackSpeed] = useState(2000);
  const [vhiPHIsLoading, setVhiPHIsLoading] = useState(false);

  // CADI agricultural drought forecast (Titiler COG time series)
  const [cadiCurrentIndex, setCadiCurrentIndex] = useState(defaultCadiFrameIndex);
  const [showCadiLegendInfo, setShowCadiLegendInfo] = useState(false);
  const [cadiPixelReadout, setCadiPixelReadout] = useState<{
    x: number;
    y: number;
    title: string;
    value: number | null;
    detail: string | null;
    label: string;
    color: string;
    loading: boolean;
    emptyMessage: string;
    error?: string;
  } | null>(null);
  const cadiPixelAbortRef = useRef<AbortController | null>(null);
  const inspectCadiPixelRef = useRef<
    (lon: number, lat: number, pixel: number[]) => void
  >(() => {});
  const rasterInspectOnRef = useRef(false);

  // Rice pest risk maps (quarterly Titiler COGs)
  const [pestRiskPestKey, setPestRiskPestKey] = useState<PestRiskPestKey>(DEFAULT_PEST_RISK_PEST_KEY);
  const [pestRiskQuarterIndex, setPestRiskQuarterIndex] = useState(0);
  const [showPestRiskLegendInfo, setShowPestRiskLegendInfo] = useState(false);
  const [floodDamageRasterActive, setFloodDamageRasterActive] = useState(false);
  const [showFloodDamageLegendInfo, setShowFloodDamageLegendInfo] = useState(false);

  // Drone NDVI prescription analytics (TiTiler POST /statistics) — rendered in left AnalysisPanel
  const [dronePrescriptionState, setDronePrescriptionState] = useState<DronePrescriptionState>({
    status: 'idle',
  });
  const [droneStatsMission, setDroneStatsMission] = useState<DroneMissionConfig | null>(null);
  const dronePrescriptionAbortRef = useRef<AbortController | null>(null);
  const prevNdviLayerVisibleRef = useRef(false);

  // CADI / pest risk municipality zonal stats — left sidebar panel
  const [multitemporalZonalState, setMultitemporalZonalState] =
    useState<MultitemporalZonalPanelState>({ status: 'idle' });
  const multitemporalZonalAbortRef = useRef<AbortController | null>(null);
  const multitemporalProvinceRef = useRef('');

  const getActiveMultitemporalZonalContext = useCallback((): {
    layer: MultitemporalZonalLayer;
    cogUrl: string;
    bidx?: number;
    frameLabel: string;
    pestLabel?: string;
  } | null => {
    if (layerVisibility.cadiDrought) {
      const frame = CADI_DROUGHT_FRAMES[cadiCurrentIndex] ?? CADI_DROUGHT_FRAMES[0];
      if (!frame?.cogUrl) return null;
      return { layer: 'cadi', cogUrl: frame.cogUrl, bidx: frame.bidx, frameLabel: frame.label };
    }
    if (layerVisibility.pestRisk) {
      const frames = getPestRiskFrames(pestRiskPestKey);
      const frame = frames[pestRiskQuarterIndex] ?? frames[0];
      if (!frame?.cogUrl) return null;
      return {
        layer: 'pestRisk',
        cogUrl: frame.cogUrl,
        frameLabel: frame.label,
        pestLabel: getPestRiskPestLabel(pestRiskPestKey),
      };
    }
    return null;
  }, [
    layerVisibility.cadiDrought,
    layerVisibility.pestRisk,
    cadiCurrentIndex,
    pestRiskPestKey,
    pestRiskQuarterIndex,
  ]);

  const clearMultitemporalZonalStats = useCallback(() => {
    multitemporalZonalAbortRef.current?.abort();
    multitemporalZonalAbortRef.current = null;
    multitemporalProvinceRef.current = '';
    setMultitemporalZonalState({ status: 'idle' });
  }, []);

  const fitMapToLonLatBbox = useCallback(
    (bbox: [number, number, number, number], maxZoom = 10) => {
      if (!mapInstanceRef.current) return;
      const [minLon, minLat, maxLon, maxLat] = bbox;
      const extent: [number, number, number, number] = [
        ...fromLonLat([minLon, minLat]),
        ...fromLonLat([maxLon, maxLat]),
      ] as [number, number, number, number];
      mapInstanceRef.current.getView().fit(extent, {
        padding: [60, 60, 60, 60],
        duration: 900,
        maxZoom,
      });
    },
    []
  );

  const zoomToProvince = useCallback(
    async (province: string, signal?: AbortSignal) => {
      try {
        const bbox = await fetchProvinceBbox(province, signal);
        if (signal?.aborted) return;
        fitMapToLonLatBbox(bbox, 10);
      } catch {
        /* zoom is best-effort; stats still load */
      }
    },
    [fitMapToLonLatBbox]
  );

  const zoomToMunicipality = useCallback(
    async (province: string, municipality: string) => {
      try {
        const geometry = await fetchMunicipalityGeometry(province, municipality);
        if (!mapInstanceRef.current) return;
        const format = new GeoJSON();
        const feature = format.readFeature(
          { type: 'Feature', geometry, properties: {} },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        ) as Feature;
        const extent = feature.getGeometry()?.getExtent();
        if (!extent) return;
        mapInstanceRef.current.getView().fit(extent, {
          padding: [60, 60, 60, 60],
          duration: 900,
          maxZoom: 12,
        });
      } catch {
        /* best-effort */
      }
    },
    []
  );

  const runMultitemporalZonalFetch = useCallback(
    async (province: string, selectedMunicipality: string | null) => {
      const active = getActiveMultitemporalZonalContext();
      if (!active) return;

      multitemporalZonalAbortRef.current?.abort();
      const ac = new AbortController();
      multitemporalZonalAbortRef.current = ac;

      setMultitemporalZonalState({ status: 'loading', progress: 0, total: 0, message: 'Loading municipalities…' });

      try {
        const municipalities = await fetchMunicipalitiesByProvince(province);
        if (ac.signal.aborted) return;

        setMultitemporalZonalState({
          status: 'loading',
          progress: 0,
          total: municipalities.length,
          message: 'Calculating municipality summaries…',
        });

        const rows = await fetchProvinceMunicipalityZonalStats(
          province,
          municipalities,
          active.cogUrl,
          (progress, total) => {
            setMultitemporalZonalState({
              status: 'loading',
              progress,
              total,
              message: 'Calculating municipality summaries…',
            });
          },
          ac.signal,
          active.bidx ?? 1,
          active.layer === 'cadi' ? CADI_FILL_NODATA : 'nan'
        );

        if (ac.signal.aborted) return;

        setMultitemporalZonalState({
          status: 'loaded',
          province,
          layer: active.layer,
          frameLabel: active.frameLabel,
          rows,
          selectedMunicipality,
        });
      } catch (err: unknown) {
        if (ac.signal.aborted) return;
        setMultitemporalZonalState({
          status: 'error',
          message: (err as Error)?.message ?? 'Could not load municipality summaries.',
        });
      }
    },
    [getActiveMultitemporalZonalContext]
  );

  const requestMultitemporalProvinceStats = useCallback(
    (province: string) => {
      multitemporalProvinceRef.current = province;
      if (!province) {
        clearMultitemporalZonalStats();
        return;
      }
      // Zoom immediately while municipality zonal stats load
      void zoomToProvince(province);
      runMultitemporalZonalFetch(province, null);
    },
    [clearMultitemporalZonalStats, runMultitemporalZonalFetch, zoomToProvince]
  );

  const selectMultitemporalMunicipality = useCallback(
    (municipality: string | null) => {
      setMultitemporalZonalState((prev) => {
        if (prev.status !== 'loaded') return prev;
        if (municipality) {
          void zoomToMunicipality(prev.province, municipality);
        }
        return { ...prev, selectedMunicipality: municipality };
      });
    },
    [zoomToMunicipality]
  );

  const usingMissionExtentForDrone = !fieldGeoJson && !!droneStatsMission;

  const clearDronePrescriptionStats = useCallback(() => {
    dronePrescriptionAbortRef.current?.abort();
    dronePrescriptionAbortRef.current = null;
    setDroneStatsMission(null);
    setDronePrescriptionState({ status: 'idle' });
  }, []);

  const showDroneOrthoIndexMap = useCallback(
    (mission: DroneMissionConfig, tileUrl: string) => {
      if (!layerManagerRef.current || !mapInstanceRef.current) return;
      const bbox = layerManagerRef.current.setDroneOrthoIndexMap(tileUrl, mission.bbox, 1);
      const [minLon, minLat, maxLon, maxLat] = bbox;
      const extent: [number, number, number, number] = [
        ...fromLonLat([minLon, minLat]),
        ...fromLonLat([maxLon, maxLat]),
      ] as [number, number, number, number];
      mapInstanceRef.current.getView().fit(extent, {
        padding: [40, 40, 40, 40],
        duration: 800,
        maxZoom: 18,
      });
    },
    []
  );

  const clearDroneOrthoIndexMap = useCallback(() => {
    layerManagerRef.current?.clearDroneOrthoIndexMap();
  }, []);

  const requestDronePrescriptionStats = useCallback(
    (mission: DroneMissionConfig) => {
      dronePrescriptionAbortRef.current?.abort();
      const ac = new AbortController();
      dronePrescriptionAbortRef.current = ac;

      const geometry = resolveAnalysisGeometry(fieldGeoJson, mission);
      if (!geometry) {
        setDroneStatsMission(mission);
        setDronePrescriptionState({ status: 'unavailable' });
        return;
      }

      setDroneStatsMission(mission);
      setDronePrescriptionState({ status: 'loading' });

      fetchDronePrescriptionAnalytics(mission.ndviCogUrl, geometry, ac.signal)
        .then((data) => {
          if (ac.signal.aborted) return;
          if (!data) {
            setDronePrescriptionState({ status: 'unavailable' });
          } else {
            setDronePrescriptionState({ status: 'loaded', data });
          }
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted) return;
          setDronePrescriptionState({
            status: 'error',
            message: 'Could not load vegetation analysis. Please try again.',
          });
        });
    },
    [fieldGeoJson]
  );

  useEffect(() => {
    const anyNdviVisible =
      layerVisibility.isabelaPhilriceNDVI || layerVisibility.caviteSilangNDVI;
    if (prevNdviLayerVisibleRef.current && !anyNdviVisible) {
      clearDronePrescriptionStats();
    }
    prevNdviLayerVisibleRef.current = anyNdviVisible;
  }, [
    layerVisibility.isabelaPhilriceNDVI,
    layerVisibility.caviteSilangNDVI,
    clearDronePrescriptionStats,
  ]);

  useEffect(() => {
    if (!onDronePrescriptionUpdate) return;
    if (dronePrescriptionState.status === 'idle' || !droneStatsMission) {
      onDronePrescriptionUpdate(null);
      return;
    }
    onDronePrescriptionUpdate({
      state: dronePrescriptionState,
      mission: droneStatsMission,
      usingMissionExtent: usingMissionExtentForDrone,
    });
  }, [
    dronePrescriptionState,
    droneStatsMission,
    usingMissionExtentForDrone,
    onDronePrescriptionUpdate,
  ]);

  useEffect(() => {
    if (!layerVisibility.cadiDrought && !layerVisibility.pestRisk) {
      clearMultitemporalZonalStats();
    }
  }, [layerVisibility.cadiDrought, layerVisibility.pestRisk, clearMultitemporalZonalStats]);

  const multitemporalFrameKey = layerVisibility.cadiDrought
    ? `cadi:${cadiCurrentIndex}`
    : layerVisibility.pestRisk
      ? `pest:${pestRiskPestKey}:${pestRiskQuarterIndex}`
      : null;
  const prevMultitemporalFrameKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!multitemporalFrameKey) {
      prevMultitemporalFrameKeyRef.current = null;
      return;
    }
    const province = multitemporalProvinceRef.current;
    if (!province) {
      prevMultitemporalFrameKeyRef.current = multitemporalFrameKey;
      return;
    }
    if (prevMultitemporalFrameKeyRef.current === multitemporalFrameKey) return;
    prevMultitemporalFrameKeyRef.current = multitemporalFrameKey;

    const selected =
      multitemporalZonalState.status === 'loaded'
        ? multitemporalZonalState.selectedMunicipality
        : null;
    runMultitemporalZonalFetch(province, selected);
  }, [
    multitemporalFrameKey,
    runMultitemporalZonalFetch,
    multitemporalZonalState,
  ]);

  useEffect(() => {
    if (!onMultitemporalZonalUpdate) return;
    const active = getActiveMultitemporalZonalContext();
    if (!active) {
      onMultitemporalZonalUpdate(null);
      return;
    }
    onMultitemporalZonalUpdate({
      state: multitemporalZonalState,
      layer: active.layer,
      frameLabel: active.frameLabel,
      pestLabel: active.pestLabel,
    });
  }, [
    multitemporalZonalState,
    getActiveMultitemporalZonalContext,
    onMultitemporalZonalUpdate,
  ]);

  useEffect(() => {
    if (!layerVisibility.cadiDrought) {
      setShowCadiLegendInfo(false);
    }
  }, [layerVisibility.cadiDrought]);

  const rasterInspectOn =
    layerVisibility.cadiDrought ||
    layerVisibility.pestRisk ||
    layerVisibility.riceGrowthStage ||
    layerVisibility.irrigationType ||
    layerVisibility.plantingMethodDry2025 ||
    layerVisibility.plantingMethodDry2024;
  rasterInspectOnRef.current = rasterInspectOn;

  useEffect(() => {
    if (rasterInspectOn) return;
    cadiPixelAbortRef.current?.abort();
    setCadiPixelReadout(null);
    const el = mapInstanceRef.current?.getTargetElement();
    if (el) el.style.cursor = '';
  }, [rasterInspectOn]);

  useEffect(() => {
    inspectCadiPixelRef.current = (lon, lat, pixel) => {
      const cadiFrame = CADI_DROUGHT_FRAMES[cadiCurrentIndex] ?? CADI_DROUGHT_FRAMES[0];
      const pestFrames = getPestRiskFrames(pestRiskPestKey);
      const pestFrame = pestFrames[pestRiskQuarterIndex] ?? pestFrames[0];
      const plantingSeason = layerVisibility.plantingMethodDry2025
        ? { label: 'Dry 2025', cogUrl: PLANTING_METHOD_COGS.dry2025 }
        : layerVisibility.plantingMethodDry2024
          ? { label: 'Dry 2024', cogUrl: PLANTING_METHOD_COGS.dry2024 }
          : null;

      let title = '';
      let emptyMessage = 'No data at this point.';
      let formatDetail: (value: number) => string | null = (value) => value.toFixed(2);
      let load: (signal: AbortSignal) => Promise<{ value: number | null; label: string; color: string }>;

      if (layerVisibility.irrigationType) {
        title = 'Irrigation type';
        emptyMessage = 'No irrigation class at this point.';
        formatDetail = (value) => `Class ${value}`;
        load = (signal) => fetchIrrigationTypePoint(lon, lat, signal);
      } else if (layerVisibility.riceGrowthStage) {
        const period = getRiceGrowthStagePeriod(riceGrowthStagePeriod);
        title = `Rice growth stage · ${period.label}`;
        emptyMessage = 'No rice growth stage at this point.';
        formatDetail = (value) => `Class ${value}`;
        load = (signal) => fetchRiceGrowthStagePoint(lon, lat, signal, period.cogUrl);
      } else if (plantingSeason) {
        title = `Planting method · ${plantingSeason.label}`;
        emptyMessage = 'No planting method at this point.';
        formatDetail = () => null;
        load = (signal) => fetchPlantingMethodPoint(plantingSeason.cogUrl, lon, lat, signal);
      } else if (layerVisibility.cadiDrought && cadiFrame) {
        title = `CADI · ${cadiFrame.label}`;
        emptyMessage = 'No drought data at this point.';
        load = (signal) => fetchCADIPointValue(lon, lat, cadiFrame.bidx, signal);
      } else if (layerVisibility.pestRisk && pestFrame) {
        title = `${getPestRiskPestLabel(pestRiskPestKey)} · ${pestFrame.label}`;
        emptyMessage = 'No occurrence data at this point.';
        load = (signal) => fetchPestPointValue(pestFrame.cogUrl, lon, lat, signal);
      } else {
        return;
      }

      cadiPixelAbortRef.current?.abort();
      const ac = new AbortController();
      cadiPixelAbortRef.current = ac;
      setCadiPixelReadout({
        x: pixel[0],
        y: pixel[1],
        title,
        value: null,
        detail: null,
        label: '',
        color: '#9ca3af',
        loading: true,
        emptyMessage,
      });
      load(ac.signal)
        .then((point) => {
          if (ac.signal.aborted) return;
          setCadiPixelReadout({
            x: pixel[0],
            y: pixel[1],
            title,
            value: point.value,
            detail: point.value == null ? null : formatDetail(point.value),
            label: point.label,
            color: point.color,
            loading: false,
            emptyMessage,
          });
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted || (err as Error)?.name === 'AbortError') return;
          setCadiPixelReadout({
            x: pixel[0],
            y: pixel[1],
            title,
            value: null,
            detail: null,
            label: 'Unavailable',
            color: '#9ca3af',
            loading: false,
            emptyMessage,
            error: 'Could not read this pixel.',
          });
        });
    };
  }, [
    layerVisibility.cadiDrought,
    layerVisibility.pestRisk,
    layerVisibility.riceGrowthStage,
    layerVisibility.irrigationType,
    layerVisibility.plantingMethodDry2025,
    layerVisibility.plantingMethodDry2024,
    riceGrowthStagePeriod,
    cadiCurrentIndex,
    pestRiskPestKey,
    pestRiskQuarterIndex,
  ]);

  useEffect(() => {
    if (!layerVisibility.pestRisk) {
      setShowPestRiskLegendInfo(false);
    }
  }, [layerVisibility.pestRisk]);

  useEffect(() => {
    if (!layerVisibility.pestRisk || !layerManagerRef.current) return;
    layerManagerRef.current.updatePestRiskLayer(
      pestRiskPestKey,
      pestRiskQuarterIndex,
      layerOpacity.pestRisk
    );
  }, [pestRiskPestKey, pestRiskQuarterIndex, layerVisibility.pestRisk, layerOpacity.pestRisk]);

  // Straw Burn Detection data state
  const [strawBurnData, setStrawBurnData] = useState<any>(null);
  const [strawBurnIsLoading, setStrawBurnIsLoading] = useState(false);

  // Custom hooks for state management
  const {
    zoomLevel,
    setZoomLevel,
    isFullscreen,
    mapCoordinates,
    setMapCoordinates,
    currentBaseLayer,
    showBaseLayerMenu,
    handleZoomIn,
    handleZoomOut,
    getCurrentLocation,
    startLocationWatch,
    stopLocationWatch,
    clearLiveLocation,
    toggleFullscreen,
    toggleBaseLayerMenu,
    handleBaseLayerChange
  } = useMapControls({ mapInstance });

  const [indexWalkActive, setIndexWalkActive] = useState(false);
  const indexWalkActionsRef = useRef<{
    start: () => void;
    stop: () => void;
    showInfo: () => void;
  } | null>(null);
  const walkControls = useMemo(
    () => ({
      startLocationWatch,
      stopLocationWatch,
      clearLiveLocation,
    }),
    [startLocationWatch, stopLocationWatch, clearLiveLocation]
  );
  const indexWalkAvailable = Boolean(
    heatMapObservationBar && indexHeatMapData?.tileUrl && fieldGeoJson
  );

  // Determine current time series data based on active layer (no longer includes drone NDVI)
  const currentTimeSeriesData = timeSeriesData;

  const {
    showTimeSeriesSlider,
    currentTimeSeriesIndex,
    isPlayingTimeSeries,
    playbackSpeed,
    handleTimeSeriesSliderChange,
    togglePlayPause,
    handleSpeedChange,
    getCurrentTimeSeriesImage
  } = useTimeSeriesControl({
    timeSeriesData: currentTimeSeriesData,
    onTimeSeriesChange: (imageData) => {
      if (layerManagerRef.current) {
        layerManagerRef.current.updateTimeSeriesLayer(imageData, layerOpacity.timeSeries);
      }
      if (onTimeSeriesSliderChange) {
        onTimeSeriesSliderChange(imageData);
      }
    }
  });


  const {
    showLegend,
    layerOpacities,
    adjustLayerOpacity,
    showLegendPanel
  } = useLayerControls();

  // Handle change detection toggle
  const handleChangeDetectionToggle = (
    enabled: boolean,
    beforeIndex?: number | null,
    afterIndex?: number | null
  ) => {
    setIsChangeDetectionMode(enabled);

    if (!enabled) {
      setChangeDetectionImages({ before: null, after: null });
      setShowChangeDetectionArrow(false);
      if (layerManagerRef.current) {
        layerManagerRef.current.clearChangeDetectionLayers();
      }
      return;
    }

    const hasPair =
      beforeIndex != null &&
      afterIndex != null &&
      timeSeriesData?.results &&
      beforeIndex !== afterIndex;

    if (hasPair) {
      const beforeImg = timeSeriesData.results![beforeIndex];
      const afterImg = timeSeriesData.results![afterIndex];

      setChangeDetectionImages({
        before: beforeImg,
        after: afterImg
      });
      setShowChangeDetectionArrow(true);

      if (layerManagerRef.current) {
        layerManagerRef.current.setupChangeDetectionLayers(
          beforeImg,
          afterImg,
          layerOpacity.timeSeries
        );
      }

      if (isPlayingTimeSeries) {
        togglePlayPause();
      }
    } else {
      // Compare mode on but waiting for a valid before/after pair
      setChangeDetectionImages({ before: null, after: null });
      setShowChangeDetectionArrow(false);
      if (layerManagerRef.current) {
        layerManagerRef.current.clearChangeDetectionLayers();
      }
    }
  };

  // Handle comparison slider position change
  const handleComparisonSliderChange = (position: number) => {
    layerManagerRef.current?.updateSwipePosition(position);
  };

  const changePairReady = Boolean(
    isChangeDetectionMode &&
    changeDetectionImages.before?.tile_url &&
    changeDetectionImages.after?.tile_url
  );

  // Drop the swipe line and split layers when the series is gone or both dates are not chosen.
  useEffect(() => {
    if (!layerManagerRef.current) return;
    const hasSeries = Boolean(timeSeriesData?.results?.length);
    if (hasSeries && changePairReady) return;
    if (!isChangeDetectionMode && !changeDetectionImages.before && !changeDetectionImages.after) return;
    layerManagerRef.current.clearChangeDetectionLayers();
    if (!hasSeries) {
      setIsChangeDetectionMode(false);
      setChangeDetectionImages({ before: null, after: null });
    }
  }, [timeSeriesData, changePairReady, isChangeDetectionMode, changeDetectionImages.before, changeDetectionImages.after]);


  // Sync WMS layer states with local state and MapLayerManager
  useEffect(() => {
    if (wmsLayerStates && layerManagerRef.current) {
      const isAnyWMSVisible =
        wmsLayerStates.yield?.visible || wmsLayerStates.vhi?.visible || wmsLayerStates.lst?.visible;

      // Update local visibility state for UI consistency
      setLayerVisibility(prev => {
        if (isWMSLayerSwitching.current) {
          return prev;
        }

        return {
          ...prev,
          yield: wmsLayerStates.yield?.visible || false,
          vhi: wmsLayerStates.vhi?.visible || false,
          lst: wmsLayerStates.lst?.visible || false,
          droneNDVI: isAnyWMSVisible ? false : prev.droneNDVI,
          ...(isAnyWMSVisible
            ? {
                isabelaPhilriceNDVI: false,
                isabelaPhilriceOrtho: false,
                caviteSilangNDVI: false,
                caviteSilangOrtho: false,
              }
            : {}),
        };
      });

      // Only NDVI_Yield uses GeoServer WMS; VHI/LST are GEE tiles via PH APIs
      const layerTypes: ('yield' | 'vhi' | 'lst')[] = ['yield'];

      layerTypes.forEach(layerType => {
        const layerState = wmsLayerStates[layerType];
        if (layerState) {
          layerManagerRef.current!.toggleWMSLayer(layerType, layerState.visible);

          if (layerState.visible && layerState.currentDate) {
            layerManagerRef.current!.updateWMSLayerTime(layerType, layerState.currentDate);
          }
        }
      });

      if (isAnyWMSVisible) {
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();
      }
    }
  }, [wmsLayerStates]);

  // Handle non-WMS layer toggle
  const handleLayerToggle = (layerType: keyof LayerVisibilityState, visible: boolean) => {
    const timeSeriesLayers = ['yield', 'vhi', 'lst', 'bsri'];

    // If enabling a time series layer, turn off other time series layers first
    if (visible && timeSeriesLayers.includes(layerType)) {
      // Turn off other time series layers
      timeSeriesLayers.forEach(layer => {
        if (layer !== layerType) {
          // For WMS layers, check wmsLayerStates directly for accurate visibility
          let isCurrentlyVisible = false;
          if (['yield', 'vhi', 'lst'].includes(layer)) {
            const wmsState = wmsLayerStates?.[layer as 'yield' | 'vhi' | 'lst'];
            isCurrentlyVisible = wmsState?.visible || false;
          } else {
            const currentVisibility = getCurrentLayerVisibility();
            isCurrentlyVisible = currentVisibility[layer as keyof LayerVisibilityState] || false;
          }
          
          if (isCurrentlyVisible) {
            // Turn off this layer - this will clear its legend, time series, and map layer
            if (['yield', 'vhi', 'lst'].includes(layer) && onWMSLayerToggle) {
              // Call parent to toggle off the WMS layer (this will use resetLayerState)
              onWMSLayerToggle(layer as 'yield' | 'vhi' | 'lst');
            } else if (layer === 'bsri') {
              // Turn off BSRI layer
              handleBSRILayerToggle(false);
            }
          }
        }
      });

      // Also clear any API-based multi-temporal tile layers (LST/VHI Philippines) when
      // switching to a different time-series layer, so only the newly selected dataset remains visible.
      if (layerManagerRef.current) {
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
        layerManagerRef.current.clearPestRiskLayer();
      }
      setLstPHData(null);
      setVhiPHData(null);
      setCadiCurrentIndex(0);
      setPestRiskQuarterIndex(0);
      setLayerVisibility(prev =>
        prev.cadiDrought || prev.pestRisk
          ? { ...prev, cadiDrought: false, pestRisk: false }
          : prev
      );
    }
    // If enabling a Rice Planting Method layer, turn off all time series layers and clear API tiles
    // If explicitly disabling LST or VHI (e.g. via tab switch or popup),
    // clear their API-based tiles so they don't remain visible.
    if (!visible && layerManagerRef.current) {
      if (layerType === 'lst') {
        layerManagerRef.current.clearLSTPHLayer();
        setLstPHData(null);
      } else if (layerType === 'vhi') {
        layerManagerRef.current.clearVHIPHLayer();
        setVhiPHData(null);
      } else if (layerType === 'cadiDrought') {
        layerManagerRef.current.clearCADIDroughtLayer();
        layerManagerRef.current.clearPestRiskLayer();
        setCadiCurrentIndex(0);
      } else if (layerType === 'pestRisk') {
        layerManagerRef.current.clearPestRiskLayer();
        setPestRiskQuarterIndex(0);
      }
    }

    // For WMS layers, delegate to parent
    // Only toggle if the current visibility state doesn't match the desired state
    if (['yield', 'vhi', 'lst'].includes(layerType)) {
      // Increment counter to track multiple simultaneous WMS layer operations
      wmsLayerSwitchCounter.current += 1;
      isWMSLayerSwitching.current = true;
      
      // Immediately update local layerVisibility state for UI responsiveness
      // This ensures radio buttons and legends update right away, even before wmsLayerStates syncs
      if (visible) {
        // If enabling a WMS layer, clear all Planting Method layers first
        // Turn off any active planting method layers when a WMS layer is enabled
        if (layerVisibility.plantingMethodDry2025 && layerManagerRef.current) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (layerVisibility.plantingMethodDry2024 && layerManagerRef.current) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (layerVisibility.riceGrowthStage && layerManagerRef.current) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (layerVisibility.irrigationType && layerManagerRef.current) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }
        if (layerManagerRef.current) {
          layerManagerRef.current.clearIsabelaPhilriceLayers();
          layerManagerRef.current.clearCaviteSilangLayers();
          layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
        }
        setCadiCurrentIndex(0);

        // If enabling a layer, clear all other WMS layers first
        const newState = {
          yield: layerType === 'yield',
          vhi: layerType === 'vhi',
          lst: layerType === 'lst',
          heatMap: layerVisibility.heatMap,
          timeSeries: layerVisibility.timeSeries,
          maize: layerVisibility.maize,
          sugarCane: layerVisibility.sugarCane,
          rice: layerVisibility.rice,
          coconut: layerVisibility.coconut,
          droneNDVI: false,
          riceForecast: layerVisibility.riceForecast,
          bsri: layerVisibility.bsri,
          strawBurn: layerVisibility.strawBurn,
          plantingMethodDry2025: false,
          plantingMethodDry2024: false,
          riceGrowthStage: false,
          irrigationType: false,
          cadiDrought: false,
          pestRisk: false,
                isabelaPhilriceNDVI: false,
          isabelaPhilriceOrtho: false,
          caviteSilangNDVI: false,
          caviteSilangOrtho: false,
          treeDetection: layerVisibility.treeDetection,
        };
        // Update ref immediately so getCurrentLayerVisibility returns latest state
        layerVisibilityRef.current = newState;
        // Update state - this will trigger re-render and parent update
        setLayerVisibility(newState);
      } else {
        // Disable only this layer. Use the pending state so a layer just turned on
        // in the same click (for example rice growth stage) is not cleared.
        setLayerVisibility((prev) => {
          const newState = {
            ...prev,
            [layerType]: false,
          };
          layerVisibilityRef.current = newState;
          return newState;
        });
      }
      
      if (onWMSLayerToggle) {
        const currentLayerState = wmsLayerStates?.[layerType as 'yield' | 'vhi' | 'lst'];
        const isCurrentlyVisible = currentLayerState?.visible || false;
        
        // Only toggle if we need to change the visibility state
        if (visible !== isCurrentlyVisible) {
          onWMSLayerToggle(layerType as 'yield' | 'vhi' | 'lst');
        }
      }
      
      // Decrement counter and reset flag only when all operations complete
      // Use a longer delay when enabling to ensure all state updates complete
      const delay = visible ? 350 : 250;
      setTimeout(() => {
        wmsLayerSwitchCounter.current = Math.max(0, wmsLayerSwitchCounter.current - 1);
        if (wmsLayerSwitchCounter.current === 0) {
          isWMSLayerSwitching.current = false;
          // Force a state sync after switching completes to ensure UI is in sync
          // This ensures legend and radio buttons update correctly
          // Use a small delay to ensure wmsLayerStates has been updated by parent
          setTimeout(() => {
            if (wmsLayerStates) {
              setLayerVisibility(prev => {
                const newState = {
                  ...prev,
                  yield: wmsLayerStates.yield?.visible || false,
                  vhi: wmsLayerStates.vhi?.visible || false,
                  lst: wmsLayerStates.lst?.visible || false,
                };
                // Only update if state actually changed to avoid unnecessary re-renders
                if (prev.yield !== newState.yield || prev.vhi !== newState.vhi || prev.lst !== newState.lst) {
                  return newState;
                }
                return prev;
              });
            }
          }, 50);
        }
      }, delay);
      
      return;
    }

    // Handle crop area layers
    if (['maize', 'sugarCane', 'rice', 'coconut'].includes(layerType) && layerManagerRef.current) {
      handleCropAreaLayerToggle(layerType as CropAreaLayerKey, visible);
      return;
    }

    // Isabela PhilRice drone COGs (Titiler) — one layer at a time
    if (
      (layerType === 'isabelaPhilriceNDVI' || layerType === 'isabelaPhilriceOrtho') &&
      layerManagerRef.current
    ) {
      const kind =
        layerType === 'isabelaPhilriceNDVI' ? ('ndvi' as const) : ('ortho' as const);

      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });

        if (layerVisibility.bsri) handleBSRILayerToggle(false);
        if (layerVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (layerVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (layerVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (layerVisibility.irrigationType) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);

        const otherKinds = (['ndvi', 'ortho'] as const).filter(k => k !== kind);
        otherKinds.forEach(k => {
          layerManagerRef.current!.toggleIsabelaPhilriceLayer(k, false, 1);
        });

        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            irrigationType: false,
            cadiDrought: false,
            pestRisk: false,
                    isabelaPhilriceNDVI: layerType === 'isabelaPhilriceNDVI',
            isabelaPhilriceOrtho: layerType === 'isabelaPhilriceOrtho',
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });

        const bbox = layerManagerRef.current.toggleIsabelaPhilriceLayer(
          kind,
          true,
          layerOpacity[layerType]
        );

        if (bbox && mapInstanceRef.current) {
          const [minLon, minLat, maxLon, maxLat] = bbox;
          const extent: [number, number, number, number] = [
            ...fromLonLat([minLon, minLat]),
            ...fromLonLat([maxLon, maxLat])
          ] as [number, number, number, number];
          mapInstanceRef.current.getView().fit(extent, {
            padding: [40, 40, 40, 40],
            duration: 1000,
            maxZoom: 18
          });
        }
      } else {
        setLayerVisibility(prev => {
          const newState = { ...prev, [layerType]: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
        layerManagerRef.current.toggleIsabelaPhilriceLayer(kind, false, layerOpacity[layerType]);
      }
      return;
    }

    // Cavite Silang drone COGs (Titiler) — one layer at a time; mutually exclusive with Isabela
    if (
      (layerType === 'caviteSilangNDVI' || layerType === 'caviteSilangOrtho') &&
      layerManagerRef.current
    ) {
      const kind =
        layerType === 'caviteSilangNDVI' ? ('ndvi' as const) : ('ortho' as const);

      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });

        if (layerVisibility.bsri) handleBSRILayerToggle(false);
        if (layerVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (layerVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (layerVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (layerVisibility.irrigationType) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);

        const otherKinds = (['ndvi', 'ortho'] as const).filter(k => k !== kind);
        otherKinds.forEach(k => {
          layerManagerRef.current!.toggleCaviteSilangLayer(k, false, 1);
        });

        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            irrigationType: false,
            cadiDrought: false,
            isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: layerType === 'caviteSilangNDVI',
            caviteSilangOrtho: layerType === 'caviteSilangOrtho',
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });

        const bbox = layerManagerRef.current.toggleCaviteSilangLayer(
          kind,
          true,
          layerOpacity[layerType]
        );

        if (bbox && mapInstanceRef.current) {
          const [minLon, minLat, maxLon, maxLat] = bbox;
          const extent: [number, number, number, number] = [
            ...fromLonLat([minLon, minLat]),
            ...fromLonLat([maxLon, maxLat])
          ] as [number, number, number, number];
          mapInstanceRef.current.getView().fit(extent, {
            padding: [40, 40, 40, 40],
            duration: 1000,
            maxZoom: 18
          });
        }
      } else {
        setLayerVisibility(prev => {
          const newState = { ...prev, [layerType]: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
        layerManagerRef.current.toggleCaviteSilangLayer(kind, false, layerOpacity[layerType]);
      }
      return;
    }

    // Handle BSRI layer
    if (layerType === 'bsri' && layerManagerRef.current) {
      handleBSRILayerToggle(visible);
      return;
    }

    // Handle rice forecast layer
    if (layerType === 'riceForecast' && layerManagerRef.current) {
      setLayerVisibility(prev => ({ ...prev, riceForecast: visible }));
      layerManagerRef.current.toggleRiceForecastLayer(visible);
      return;
    }

    // Handle straw burn layer
    if (layerType === 'strawBurn' && layerManagerRef.current) {
      setLayerVisibility(prev => ({ ...prev, strawBurn: visible }));
      if (visible) {
        layerManagerRef.current.toggleStrawBurnLayer(visible);
      } else {
        handleStrawBurnDataClear();
      }
      return;
    }

    // Tree Detection is not displayed on map - skip layer toggle
    if (layerType === 'treeDetection') {
      // Don't add to map, just keep state for panel display
      return;
    }

    // Handle CADI agricultural drought forecast (Titiler COG time series)
    if (layerType === 'cadiDrought' && layerManagerRef.current) {
      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });

        if (layerVisibility.bsri) handleBSRILayerToggle(false);
        if (layerVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (layerVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (layerVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (layerVisibility.irrigationType) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);

        const cadiIndex = defaultCadiFrameIndex();
        setCadiCurrentIndex(cadiIndex);
        setPestRiskQuarterIndex(0);
        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            irrigationType: false,
            isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
            pestRisk: false,
                    cadiDrought: true,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });

        layerManagerRef.current.updateCADIDroughtLayer(cadiIndex, layerOpacity.cadiDrought);

        const bbox = layerManagerRef.current.getCADIDroughtBbox();
        if (bbox && mapInstanceRef.current) {
          const [minLon, minLat, maxLon, maxLat] = bbox;
          const extent: [number, number, number, number] = [
            ...fromLonLat([minLon, minLat]),
            ...fromLonLat([maxLon, maxLat])
          ] as [number, number, number, number];
          mapInstanceRef.current.getView().fit(extent, {
            padding: [40, 40, 40, 40],
            duration: 1000,
            maxZoom: 6,
          });
        }
      } else {
        layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
        setCadiCurrentIndex(0);
        setLayerVisibility(prev => {
          const newState = { ...prev, cadiDrought: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      }
      return;
    }

    // Handle rice pest risk maps (Titiler COG quarterly time series)
    if (layerType === 'pestRisk' && layerManagerRef.current) {
      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });

        if (layerVisibility.bsri) handleBSRILayerToggle(false);
        if (layerVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (layerVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (layerVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (layerVisibility.irrigationType) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);

        setPestRiskQuarterIndex(0);
        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            irrigationType: false,
            isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
            cadiDrought: false,
            pestRisk: true,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });

        layerManagerRef.current.updatePestRiskLayer(
          pestRiskPestKey,
          0,
          layerOpacity.pestRisk
        );

        const bbox = layerManagerRef.current.getPestRiskBbox();
        if (bbox && mapInstanceRef.current) {
          const [minLon, minLat, maxLon, maxLat] = bbox;
          const extent: [number, number, number, number] = [
            ...fromLonLat([minLon, minLat]),
            ...fromLonLat([maxLon, maxLat])
          ] as [number, number, number, number];
          mapInstanceRef.current.getView().fit(extent, {
            padding: [40, 40, 40, 40],
            duration: 1000,
            maxZoom: 6,
          });
        }
      } else {
        layerManagerRef.current.clearPestRiskLayer();
        setPestRiskQuarterIndex(0);
        setLayerVisibility(prev => {
          const newState = { ...prev, pestRisk: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      }
      return;
    }

    if (layerType === 'riceGrowthStage' && layerManagerRef.current) {
      const currentVisibility = getCurrentLayerVisibility();
      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });
        if (currentVisibility.bsri) handleBSRILayerToggle(false);
        if (currentVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (currentVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
        layerManagerRef.current.clearPestRiskLayer();
        layerManagerRef.current.clearIrrigationTypeLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);
        setPestRiskQuarterIndex(0);
        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            cadiDrought: false,
            pestRisk: false,
                    isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
            irrigationType: false,
            riceGrowthStage: true,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      } else {
        setLayerVisibility(prev => {
          const newState = { ...prev, riceGrowthStage: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      }

      layerManagerRef.current.setRiceGrowthStageLoadingListener(setRiceGrowthStageLoading);
      const period = getRiceGrowthStagePeriod(riceGrowthStagePeriodRef.current);
      const bbox = layerManagerRef.current.toggleRiceGrowthStageLayer(
        visible,
        layerOpacity.riceGrowthStage,
        period.cogUrl,
        period.bbox
      );
      if (visible && bbox && mapInstanceRef.current) {
        const [minLon, minLat, maxLon, maxLat] = bbox;
        const extent: [number, number, number, number] = [
          ...fromLonLat([minLon, minLat]),
          ...fromLonLat([maxLon, maxLat])
        ] as [number, number, number, number];
        mapInstanceRef.current.getView().fit(extent, {
          padding: [40, 40, 40, 40],
          duration: 1000,
          maxZoom: 7
        });
      }
      return;
    }

    if (layerType === 'irrigationType' && layerManagerRef.current) {
      const currentVisibility = getCurrentLayerVisibility();
      if (visible) {
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });
        if (currentVisibility.bsri) handleBSRILayerToggle(false);
        if (currentVisibility.plantingMethodDry2025) {
          layerManagerRef.current.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
        }
        if (currentVisibility.plantingMethodDry2024) {
          layerManagerRef.current.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
        }
        if (currentVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
        layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);
        setPestRiskQuarterIndex(0);
        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            cadiDrought: false,
            pestRisk: false,
            isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
            irrigationType: true,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      } else {
        setLayerVisibility(prev => {
          const newState = { ...prev, irrigationType: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      }

      layerManagerRef.current.setIrrigationTypeLoadingListener(setIrrigationTypeLoading);
      const bbox = layerManagerRef.current.toggleIrrigationTypeLayer(visible, layerOpacity.irrigationType);
      if (visible && bbox && mapInstanceRef.current) {
        const [minLon, minLat, maxLon, maxLat] = bbox;
        const extent: [number, number, number, number] = [
          ...fromLonLat([minLon, minLat]),
          ...fromLonLat([maxLon, maxLat])
        ] as [number, number, number, number];
        mapInstanceRef.current.getView().fit(extent, {
          padding: [40, 40, 40, 40],
          duration: 1000,
          maxZoom: 7
        });
      }
      return;
    }

    // Handle Planting Method Map layers (Dry 2024 / Dry 2025)
    if (layerType.startsWith('plantingMethod') && layerManagerRef.current) {
      const region =
        layerType === 'plantingMethodDry2025'
          ? 'isabela'
          : layerType === 'plantingMethodDry2024'
            ? 'laguna'
            : 'isabela';

      const currentVisibility = getCurrentLayerVisibility();

      if (visible) {
        // Clear all WMS layers (yield, vhi, lst) first
        const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
        wmsLayers.forEach(wmsLayer => {
          const wmsState = wmsLayerStates?.[wmsLayer];
          if (wmsState?.visible && onWMSLayerToggle) {
            onWMSLayerToggle(wmsLayer);
          }
        });

        // Turn off other multi-temporal layers
        if (currentVisibility.bsri) handleBSRILayerToggle(false);
        layerManagerRef.current.clearIsabelaPhilriceLayers();
        layerManagerRef.current.clearCaviteSilangLayers();

        // Turn off the OTHER planting method layer (only one active at a time)
        const otherPlantingKey = layerType === 'plantingMethodDry2025' ? 'plantingMethodDry2024' : 'plantingMethodDry2025';
        const otherRegion = layerType === 'plantingMethodDry2025' ? 'laguna' : 'isabela';
        if (currentVisibility[otherPlantingKey]) {
          layerManagerRef.current.togglePlantingMethodLayer(otherRegion, false, layerOpacity[otherPlantingKey]);
        }
        if (currentVisibility.riceGrowthStage) {
          layerManagerRef.current.clearRiceGrowthStageLayer();
        }
        if (currentVisibility.irrigationType) {
          layerManagerRef.current.clearIrrigationTypeLayer();
        }

        // Clear API-based multi-temporal tile layers (LST/VHI Philippines) and CADI
        layerManagerRef.current.clearLSTPHLayer();
        layerManagerRef.current.clearVHIPHLayer();
        layerManagerRef.current.clearCADIDroughtLayer();
        layerManagerRef.current.clearPestRiskLayer();
        setLstPHData(null);
        setVhiPHData(null);
        setCadiCurrentIndex(0);
        setPestRiskQuarterIndex(0);

        // Update local state immediately for UI responsiveness
        setLayerVisibility(prev => {
          const newState = {
            ...prev,
            yield: false,
            vhi: false,
            lst: false,
            droneNDVI: false,
            bsri: false,
            plantingMethodDry2025: false,
            plantingMethodDry2024: false,
            riceGrowthStage: false,
            irrigationType: false,
            cadiDrought: false,
            pestRisk: false,
                    isabelaPhilriceNDVI: false,
            isabelaPhilriceOrtho: false,
            caviteSilangNDVI: false,
            caviteSilangOrtho: false,
            [layerType]: true,
          } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      } else {
        setLayerVisibility(prev => {
          const newState = { ...prev, [layerType]: false } as LayerVisibilityState;
          layerVisibilityRef.current = newState;
          return newState;
        });
      }

      const bbox = layerManagerRef.current.togglePlantingMethodLayer(region, visible, layerOpacity[layerType]);

      // Zoom to Philippines bounds when activating a planting method layer
      if (visible && bbox && mapInstanceRef.current) {
        const [minLon, minLat, maxLon, maxLat] = bbox;
        const extent: [number, number, number, number] = [
          ...fromLonLat([minLon, minLat]),
          ...fromLonLat([maxLon, maxLat])
        ] as [number, number, number, number];
        mapInstanceRef.current.getView().fit(extent, {
          padding: [40, 40, 40, 40],
          duration: 1000,
          maxZoom: 7
        });
      }
      return;
    }

    // Handle non-WMS layers locally
    setLayerVisibility(prev => ({ ...prev, [layerType]: visible }));

    if (layerManagerRef.current) {
      if (layerType === 'heatMap' || layerType === 'timeSeries') {
        layerManagerRef.current.toggleLayerVisibility(layerType, layerOpacity[layerType]);
      }
    }
  };

  // Handle crop area layer toggle with loading states
  const handleCropAreaLayerToggle = async (cropType: CropAreaLayerKey, visible: boolean) => {
    if (!layerManagerRef.current || !mapInstanceRef.current) return;

    if (visible) {
      setCropAreaLoadingLayers(prev => new Set([...prev, cropType]));
      const [minLon, minLat, maxLon, maxLat] =
        cropType === 'maize' ? MAIZE_CROP_BBOX
        : cropType === 'rice' ? RICE_CROP_BBOX
        : cropType === 'sugarCane' ? SUGARCANE_CROP_BBOX
        : COCONUT_CROP_BBOX;
        const extent: [number, number, number, number] = [
          ...fromLonLat([minLon, minLat]),
          ...fromLonLat([maxLon, maxLat]),
        ] as [number, number, number, number];
        mapInstanceRef.current.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 1000,
          maxZoom: cropType === 'coconut' ? 11 : 7,
        });
    }

    // Update local state immediately for UI feedback. Only one crop map is on at a time.
    setLayerVisibility(prev => {
      const next = { ...prev, [cropType]: visible };
      if (visible) {
        (['maize', 'sugarCane', 'rice', 'coconut'] as const).forEach((key) => {
          if (key !== cropType) next[key] = false;
        });
      }
      layerVisibilityRef.current = next;
      return next;
    });

    if (visible) {
      (['maize', 'sugarCane', 'rice', 'coconut'] as const).forEach((key) => {
        if (key !== cropType) {
          void layerManagerRef.current?.toggleCropAreaLayer(key, false);
        }
      });
    }

    try {
      // Use the MapLayerManager to toggle the crop area layer
      await layerManagerRef.current.toggleCropAreaLayer(
        cropType,
        visible,
        // Loading start callback
        () => {
          setCropAreaLoadingLayers(prev => new Set([...prev, cropType]));
          return 'loading';
        },
        // Loading end callback
        (toastId: string, success: boolean, message: string) => {
          // Remove loading state when done (success or failure)
          setCropAreaLoadingLayers(prev => {
            const newSet = new Set(prev);
            newSet.delete(cropType);
            return newSet;
          });

          if (!success) {
            // Revert local state if loading failed
            setLayerVisibility(prev => ({ ...prev, [cropType]: false }));
          }
        }
      );
    } catch (error) {
      console.error(`Error toggling ${cropType} crop area layer:`, error);
      // Remove loading state and revert local state on error
      setCropAreaLoadingLayers(prev => {
        const newSet = new Set(prev);
        newSet.delete(cropType);
        return newSet;
      });
      setLayerVisibility(prev => ({ ...prev, [cropType]: false }));
    }
  };

  // Handle flood damage layer show
  const handleFloodLayerShow = (layerInfo: { wmsUrl: string; layerName: string; bbox: [number, number, number, number] }) => {
    if (mapInstanceRef.current) {
      const [minLon, minLat, maxLon, maxLat] = layerInfo.bbox;
      const extent = [
        ...fromLonLat([minLon, minLat]),
        ...fromLonLat([maxLon, maxLat])
      ];

      mapInstanceRef.current.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        duration: 1000
      });
    }
  };

  // Handle tree detection data update from LayerTogglePanel
  const handleTreeDetectionDataUpdate = (data: any) => {
    setTreeDetectionData(data);

    // Update points layer: show detected locations as small pins, or clear when no data
    if (layerManagerRef.current) {
      layerManagerRef.current.updateTreeDetectionPointsLayer(data?.geojson ?? null);
    }

    // Zoom to GeoJSON bounds when tree detection returns geojson
    if (data?.success && data?.geojson?.features?.length && mapInstanceRef.current) {
      const features = data.geojson.features;
      let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;

      const extractCoords = (geom: any): [number, number][] => {
        if (!geom || !geom.coordinates) return [];
        if (geom.type === 'Point') return [geom.coordinates];
        if (geom.type === 'MultiPoint') return geom.coordinates;
        if (geom.type === 'LineString') return geom.coordinates;
        if (geom.type === 'Polygon') return geom.coordinates[0] || [];
        if (geom.type === 'MultiPolygon') return (geom.coordinates[0] && geom.coordinates[0][0]) ? geom.coordinates.flat(2) : [];
        return [];
      };

      for (const f of features) {
        const coords = extractCoords(f?.geometry);
        for (const [lon, lat] of coords) {
          if (typeof lon === 'number' && typeof lat === 'number') {
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
          }
        }
      }

      if (minLon !== Infinity && maxLat !== -Infinity) {
        // Single point or very small extent: add padding in degrees so view is usable
        const padLon = Math.max((maxLon - minLon) * 0.5 || 0.001, 0.001);
        const padLat = Math.max((maxLat - minLat) * 0.5 || 0.001, 0.001);
        const extent = [
          ...fromLonLat([minLon - padLon, minLat - padLat]),
          ...fromLonLat([maxLon + padLon, maxLat + padLat])
        ];
        mapInstanceRef.current.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 1000
        });
      }
    }
  };

  /** Crop-field boundaries from Digisaka GPT (georeferenced GeoJSON, projected CRS). */
  const handleCropfieldDelineationUpdate = (data: any) => {
    if (!layerManagerRef.current) return;
    if (!data) {
      layerManagerRef.current.updateCropfieldDelineationLayer(null, null);
      return;
    }
    const gj = data.geojson_wgs84 ?? data.geojson ?? null;
    const crsStr = data.geojson_wgs84 ? 'EPSG:4326' : data.crs ?? null;
    layerManagerRef.current.updateCropfieldDelineationLayer(gj, crsStr);
  };

  const setCropfieldDelineationSelectedIndex = (index: number | null) => {
    layerManagerRef.current?.setCropfieldDelineationSelectedIndex(index);
  };

  const setCropfieldDelineationCheckedIndexes = (indexes: number[]) => {
    layerManagerRef.current?.setCropfieldDelineationCheckedIndexes(indexes);
  };

  const setCropfieldDelineationMultiSelectMode = (active: boolean) => {
    layerManagerRef.current?.setCropfieldDelineationMultiSelectMode(active);
  };

  const startCropfieldVertexEdit = (featureIndex: number) =>
    layerManagerRef.current?.startCropfieldVertexEdit(featureIndex) ?? false;

  const cancelCropfieldVertexEdit = () => {
    layerManagerRef.current?.cancelCropfieldVertexEdit();
  };

  const undoCropfieldVertexEdit = () =>
    layerManagerRef.current?.undoCropfieldVertexEdit() ?? false;

  const redoCropfieldVertexEdit = () =>
    layerManagerRef.current?.redoCropfieldVertexEdit() ?? false;

  const canUndoCropfieldVertexEdit = () =>
    layerManagerRef.current?.canUndoCropfieldVertexEdit() ?? false;

  const canRedoCropfieldVertexEdit = () =>
    layerManagerRef.current?.canRedoCropfieldVertexEdit() ?? false;

  const setCropfieldVertexHistoryListener = (
    listener: ((state: { canUndo: boolean; canRedo: boolean }) => void) | null
  ) => {
    layerManagerRef.current?.setCropfieldVertexHistoryListener(listener);
  };

  const confirmCropfieldVertexEdit = () =>
    layerManagerRef.current?.confirmCropfieldVertexEdit() ?? null;

  const applyCropfieldVertexEditInPlace = () =>
    layerManagerRef.current?.applyCropfieldVertexEditInPlace() ?? null;

  const confirmCropfieldDelineationFeature = (featureIndex: number) =>
    layerManagerRef.current?.confirmCropfieldDelineationFeature(featureIndex) ?? null;

  const confirmCropfieldDelineationFeatures = (featureIndices: number[]) =>
    layerManagerRef.current?.confirmCropfieldDelineationFeatures(featureIndices) ?? null;

  const getCropfieldDelineationCount = () =>
    layerManagerRef.current?.getCropfieldDelineationCount() ?? 0;

  const getCropfieldDelineationFeatureCollectionGeoJson = () =>
    layerManagerRef.current?.getCropfieldDelineationFeatureCollectionGeoJson() ?? null;

  const removeCropfieldDelineationFeatureAt = (featureIndex: number) =>
    layerManagerRef.current?.removeCropfieldDelineationFeatureAt(featureIndex) ?? 0;

  const removeCropfieldDelineationFeaturesAt = (featureIndices: number[]) =>
    layerManagerRef.current?.removeCropfieldDelineationFeaturesAt(featureIndices) ?? 0;

  const isCropfieldVertexEditActive = () =>
    layerManagerRef.current?.isCropfieldVertexEditActive() ?? false;

  /** Crown segmentation polygons — prefer WGS84 for the map when present. */
  const handleCrownSegmentationMapUpdate = (data: any) => {
    if (!layerManagerRef.current) return;
    if (!data) {
      layerManagerRef.current.updateCrownSegmentationLayer(null, null);
      return;
    }
    const gj = data.geojson_wgs84 ?? data.geojson ?? null;
    const crsStr = data.geojson_wgs84 ? 'EPSG:4326' : data.crs ?? null;
    layerManagerRef.current.updateCrownSegmentationLayer(gj, crsStr);
  };

  // Handle forecast data update
  const handleForecastDataUpdate = (forecastData: any) => {
    setRiceForecastData(forecastData);

    // If null, clear the forecast layer
    if (!forecastData && layerManagerRef.current) {
      layerManagerRef.current.updateRiceForecastLayer(null, 0);
      setLayerVisibility(prev => ({ ...prev, riceForecast: false }));
      return;
    }

    // Ensure rice forecast visibility is turned on when we have forecast data
    setLayerVisibility(prev => ({ ...prev, riceForecast: true }));

    // Auto-zoom to tile bounds if available
    if (forecastData?.forecasts) {
      // Get the first forecast month data
      const firstMonthKey = Object.keys(forecastData.forecasts)[0];
      const firstMonthData = forecastData.forecasts[firstMonthKey];

      if (firstMonthData?.tile_bounds?.coordinates?.[0]) {
        const coordinates = firstMonthData.tile_bounds.coordinates[0];

        if (Array.isArray(coordinates) && coordinates.length >= 4) {
          // Extract bounding box from polygon coordinates
          // coordinates format: [[lon, lat], [lon, lat], ...]
          const lons = coordinates.map(coord => coord[0]);
          const lats = coordinates.map(coord => coord[1]);
          const minLon = Math.min(...lons);
          const maxLon = Math.max(...lons);
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);

          if (mapInstanceRef.current) {
            // Transform the bounding box corners to map projection
            const extent = [
              ...fromLonLat([minLon, minLat]),
              ...fromLonLat([maxLon, maxLat])
            ];

            // Fit the view to the bounding box
            mapInstanceRef.current.getView().fit(extent, {
              padding: [50, 50, 50, 50],
              duration: 1000
            });
          } else {
            console.warn('MapView: mapInstanceRef.current is null, cannot zoom');
          }
        } else {
          console.warn('MapView: Invalid tile bounds coordinates:', coordinates);
        }
      } else {
        console.warn('MapView: No tile_bounds data found in first month forecast');
      }
    } else {
      console.warn('MapView: No forecasts data found in forecast response');
    }
  };

  // Fetch BSRI data from API
  const fetchBSRIData = async (dateRange: { start_date: string; end_date: string }) => {
    if (!mapInstanceRef.current || !layerManagerRef.current) return;

    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);

    setBsriIsLoading(true);
    try {
      const response = await fetch(
        DIGISAKA_API_ORIGIN + '/api/bsritimeseriesimages/',
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify(range),
        })
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();

      if (data.success && data.tiles && data.tiles.length > 0) {
        setBsriData(data);
        setBsriCurrentIndex(0);

        // Zoom to AOI centroid if available
        if (data.aoi_centroid && mapInstanceRef.current) {
          const [lon, lat] = data.aoi_centroid;
          mapInstanceRef.current.getView().animate({
            center: fromLonLat([lon, lat]),
            zoom: 12,
            duration: 1000
          });
        }

        // Always update layer with data so legend is available immediately (avoids stale closure:
        // layerVisibility.bsri may be false when fetch completes even though user just enabled it)
        layerManagerRef.current.updateBSRILayer(data, 0, layerOpacity.bsri);
      }
    } catch (error) {
      console.error('Error fetching BSRI data:', error);
    } finally {
      setBsriIsLoading(false);
    }
  };

  // Fetch LST Philippines (MODIS) data from API
  const fetchLSTPHData = async (dateRange: { start_date: string; end_date: string }) => {
    if (!mapInstanceRef.current || !layerManagerRef.current) return;

    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);
    console.log('[LST] fetchLSTPHData called with dateRange', range);
    setLstPHIsLoading(true);
    try {
      console.log('[LST] Sending POST request to /api/lstphiltimeseriesimages/');
      const response = await fetch(
        DIGISAKA_API_ORIGIN + '/api/lstphiltimeseriesimages/',
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify(range),
        })
      );

      if (!response.ok) {
        console.error('[LST] API error status', response.status);
        throw new Error(`LST Philippines API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();
      console.log('[LST] API response received', data);

      // As soon as we have a response, clear any previously loaded VHI Philippines data/layer
      // so the old layer disappears immediately while we apply the new LST tiles.
      setVhiPHData(null);
      if (layerManagerRef.current) {
        layerManagerRef.current.clearVHIPHLayer();
      }

      if (data.success && data.tiles && data.tiles.length > 0) {
        console.log('[LST] Valid tiles received, tiles count:', data.tiles.length);
        setLstPHData(data);
        setLstPHCurrentIndex(0);

        // Mark LST as active so hasLoadedData / popup / status indicators work
        setLayerVisibility(prev => {
          const updated = { ...prev, lst: true, vhi: false };
          layerVisibilityRef.current = updated;
          return updated;
        });

        // Zoom to AOI centroid if available
        if (data.aoi_centroid && mapInstanceRef.current) {
          const [lon, lat] = data.aoi_centroid;
          mapInstanceRef.current.getView().animate({
            center: fromLonLat([lon, lat]),
            zoom: 6,
            duration: 1000
          });
        }

        // Update LST Philippines layer immediately so legend/tiles are visible
        console.log('[LST] Updating LST PH layer on map');
        layerManagerRef.current.updateLSTPHLayer(data, 0, layerOpacity.lst);
      } else {
        console.warn('[LST] API returned no tiles or success=false', data);
      }
    } catch (error) {
      console.error('Error fetching LST Philippines data:', error);
    } finally {
      console.log('[LST] fetchLSTPHData finished, clearing loading state');
      setLstPHIsLoading(false);
    }
  };

  // Fetch VHI Philippines (agricultural drought) data from API
  const fetchVHIPHData = async (dateRange: { start_date: string; end_date: string }) => {
    if (!mapInstanceRef.current || !layerManagerRef.current) return;

    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);
    console.log('[VHI] fetchVHIPHData called with dateRange', range);
    setVhiPHIsLoading(true);
    try {
      console.log('[VHI] Sending POST request to /api/vhiphiltimeseriesimages/');
      const response = await fetch(
        DIGISAKA_API_ORIGIN + '/api/vhiphiltimeseriesimages/',
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify(range),
        })
      );

      if (!response.ok) {
        console.error('[VHI] API error status', response.status);
        throw new Error(`VHI Philippines API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();
      console.log('[VHI] API response received', data);

      // As soon as we have a response, clear any previously loaded LST Philippines data/layer
      // so the old layer disappears immediately while we apply the new VHI tiles.
      setLstPHData(null);
      if (layerManagerRef.current) {
        layerManagerRef.current.clearLSTPHLayer();
      }

      if (data.success && data.tiles && data.tiles.length > 0) {
        console.log('[VHI] Valid tiles received, tiles count:', data.tiles.length);
        setVhiPHData(data);
        setVhiPHCurrentIndex(0);

        // Mark VHI as active so hasLoadedData / popup / status indicators work
        setLayerVisibility(prev => {
          const updated = { ...prev, vhi: true, lst: false };
          layerVisibilityRef.current = updated;
          return updated;
        });

        // Zoom to AOI centroid if available
        if (data.aoi_centroid && mapInstanceRef.current) {
          const [lon, lat] = data.aoi_centroid;
          mapInstanceRef.current.getView().animate({
            center: fromLonLat([lon, lat]),
            zoom: 6,
            duration: 1000
          });
        }

        // Update VHI Philippines layer immediately so legend/tiles are visible
        console.log('[VHI] Updating VHI PH layer on map');
        layerManagerRef.current.updateVHIPHLayer(data, 0, layerOpacity.vhi);
      } else {
        console.warn('[VHI] API returned no tiles or success=false', data);
      }
    } catch (error) {
      console.error('Error fetching VHI Philippines data:', error);
    } finally {
      console.log('[VHI] fetchVHIPHData finished, clearing loading state');
      setVhiPHIsLoading(false);
    }
  };

  // Handle BSRI layer toggle
  const handleBSRILayerToggle = async (visible: boolean) => {
    if (!mapInstanceRef.current || !layerManagerRef.current) return;

    if (visible) {
      // Update BSRI state
      setLayerVisibility(prev => ({ ...prev, bsri: true }));

      // Only show layer if data is already loaded
      // Data will be loaded via handleBSRIDateRangeUpdate when user clicks "Load Data"
      if (bsriData) {
        layerManagerRef.current.updateBSRILayer(bsriData, bsriCurrentIndex, layerOpacity.bsri);
      }
    } else {
      // If disabling, hide the layer
      setLayerVisibility(prev => ({ ...prev, bsri: false }));
      layerManagerRef.current.toggleBSRILayer(false);
    }
  };

  // Handle BSRI date range update
  const handleBSRIDateRangeUpdate = async (dateRange: { start_date: string; end_date: string }) => {
    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);
    setBsriDateRange(range);

    // Refetch data with new date range
    await fetchBSRIData(range);
  };

  // Handle LST Philippines date range update
  const handleLSTPHDateRangeUpdate = async (dateRange: { start_date: string; end_date: string }) => {
    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);
    await fetchLSTPHData(range);
  };

  // Handle VHI Philippines date range update
  const handleVHIPHDateRangeUpdate = async (dateRange: { start_date: string; end_date: string }) => {
    const range = normalizeDateRange(dateRange.start_date, dateRange.end_date);
    await fetchVHIPHData(range);
  };

  // Clear all multi-temporal API tiles (LST/VHI Philippines) explicitly – used when switching
  // between major panels (e.g., from Multi-Temporal Maps to Rice Yield Forecast).
  const clearMultiTemporalAPITiles = () => {
    if (layerManagerRef.current) {
      layerManagerRef.current.clearLSTPHLayer();
      layerManagerRef.current.clearVHIPHLayer();
      layerManagerRef.current.clearCADIDroughtLayer();
      layerManagerRef.current.clearPestRiskLayer();
    }
    setLstPHData(null);
    setVhiPHData(null);
    setCadiCurrentIndex(0);
    setPestRiskQuarterIndex(0);
    setLayerVisibility(prev =>
      prev.cadiDrought || prev.pestRisk
        ? { ...prev, cadiDrought: false, pestRisk: false }
        : prev
    );
  };

  // Handle Straw Burn Detection data clear
  const handleStrawBurnDataClear = () => {
    setStrawBurnData(null);
    if (layerManagerRef.current) {
      layerManagerRef.current.updateStrawBurnLayer(null, 0);
    }
    setLayerVisibility(prev => ({ ...prev, strawBurn: false }));
  };

  // Handle Straw Burn Detection data fetch (municipality or province)
  const handleStrawBurnDataFetch = async (payload: StrawBurnFetchPayload) => {
    if (!mapInstanceRef.current || !layerManagerRef.current) return;

    const isProvince = payload.mode === 'province';
    let start = payload.start_date;
    let end = payload.end_date;
    if (start && end) {
      const n = normalizeDateRange(start, end);
      start = n.start_date;
      end = n.end_date;
    }

    const body = isProvince
      ? { province_name: payload.province_name, ...(start ? { start_date: start } : {}), ...(end ? { end_date: end } : {}) }
      : {
          municipality_name: payload.municipality_name,
          ...(start ? { start_date: start } : {}),
          ...(end ? { end_date: end } : {})
        };

    const url = `${FARM_WEBGIS_API_BASE_URL}/api/burn-detection/${isProvince ? 'province' : 'municipality'}/`;

    setStrawBurnIsLoading(true);
    try {
      const response = await fetch(
        url,
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify(body),
        })
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();

      if (data.success && data.tile_url) {
        setStrawBurnData(data);

        // Enable the layer if not already enabled
        if (!layerVisibility.strawBurn) {
          setLayerVisibility(prev => ({ ...prev, strawBurn: true }));
        }

        // Update the layer with new data
        layerManagerRef.current.updateStrawBurnLayer(data, layerOpacity.strawBurn);

        // Zoom to tile bounds if available
        if (data.tile_bounds?.coordinates?.[0] && mapInstanceRef.current) {
          const coordinates = data.tile_bounds.coordinates[0];
          const lons = coordinates.map((coord: number[]) => coord[0]);
          const lats = coordinates.map((coord: number[]) => coord[1]);
          const minLon = Math.min(...lons);
          const maxLon = Math.max(...lons);
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);

          const extent = [
            ...fromLonLat([minLon, minLat]),
            ...fromLonLat([maxLon, maxLat])
          ];

          mapInstanceRef.current.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            duration: 1000
          });
        }
      }
    } catch (error) {
      console.error('Error fetching Straw Burn Detection data:', error);
    } finally {
      setStrawBurnIsLoading(false);
    }
  };

  // Handle opacity change
  /**
   * Toggle one registry drone layer (see `droneSites.ts`). Mirrors the Isabela / Cavite handlers:
   * turning one on switches off the other map layers and every other drone layer, then fits the flight.
   */
  const handleDroneSiteLayerToggle = (layerId: string, visible: boolean) => {
    const manager = layerManagerRef.current;
    if (!manager) return;

    if (!visible) {
      manager.clearDroneSiteLayers();
      return;
    }

    const droneLayer = findDroneLayer(layerId);
    if (!droneLayer) return;

    const current = layerVisibilityRef.current ?? layerVisibility;

    const wmsLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'];
    wmsLayers.forEach((wmsLayer) => {
      if (wmsLayerStates?.[wmsLayer]?.visible && onWMSLayerToggle) {
        onWMSLayerToggle(wmsLayer);
      }
    });

    if (current.bsri) handleBSRILayerToggle(false);
    if (current.plantingMethodDry2025) {
      manager.togglePlantingMethodLayer('isabela', false, layerOpacity.plantingMethodDry2025);
    }
    if (current.plantingMethodDry2024) {
      manager.togglePlantingMethodLayer('laguna', false, layerOpacity.plantingMethodDry2024);
    }
    if (current.riceGrowthStage) manager.clearRiceGrowthStageLayer();
    if (current.irrigationType) manager.clearIrrigationTypeLayer();
    manager.clearLSTPHLayer();
    manager.clearVHIPHLayer();
    manager.clearCADIDroughtLayer();
    manager.clearPestRiskLayer();
    setLstPHData(null);
    setVhiPHData(null);
    setCadiCurrentIndex(0);

    setLayerVisibility((prev) => {
      const newState = {
        ...prev,
        yield: false,
        vhi: false,
        lst: false,
        droneNDVI: false,
        bsri: false,
        plantingMethodDry2025: false,
        plantingMethodDry2024: false,
        riceGrowthStage: false,
        irrigationType: false,
        cadiDrought: false,
        isabelaPhilriceNDVI: false,
        isabelaPhilriceOrtho: false,
        caviteSilangNDVI: false,
        caviteSilangOrtho: false,
      } as LayerVisibilityState;
      layerVisibilityRef.current = newState;
      return newState;
    });

    const bbox = manager.toggleDroneSiteLayer(droneLayer, true, droneSiteOpacity[layerId] ?? 1);
    if (bbox && mapInstanceRef.current) {
      const [minLon, minLat, maxLon, maxLat] = bbox;
      const extent = [...fromLonLat([minLon, minLat]), ...fromLonLat([maxLon, maxLat])] as [
        number,
        number,
        number,
        number,
      ];
      mapInstanceRef.current.getView().fit(extent, {
        padding: [40, 40, 40, 40],
        duration: 1000,
        maxZoom: 19,
      });
    }
  };

  const handleDroneSiteOpacityChange = (layerId: string, opacity: number) => {
    setDroneSiteOpacity((prev) => ({ ...prev, [layerId]: opacity }));
    layerManagerRef.current?.setDroneSiteLayerOpacity(layerId, opacity);
  };

  // Latest closures behind stable wrappers: `onLayerControlReady` only re-fires on some state changes.
  const droneSiteToggleRef = useRef(handleDroneSiteLayerToggle);
  droneSiteToggleRef.current = handleDroneSiteLayerToggle;
  const stableDroneSiteToggle = useCallback(
    (layerId: string, visible: boolean) => droneSiteToggleRef.current(layerId, visible),
    []
  );
  const stableDroneSiteOpacityChange = useCallback(
    (layerId: string, opacity: number) => handleDroneSiteOpacityChange(layerId, opacity),
    []
  );
  const clearDroneSiteLayers = useCallback(() => {
    layerManagerRef.current?.clearDroneSiteLayers();
  }, []);

  const handleOpacityChange = (layerType: keyof LayerOpacityState, opacity: number) => {
    setLayerOpacity(prev => ({ ...prev, [layerType]: opacity }));

    if (layerManagerRef.current) {
      if (layerType === 'yield') {
        layerManagerRef.current.setWMSLayerOpacity('yield', opacity);
      } else if (layerType === 'lst') {
        layerManagerRef.current.setLSTPHLayerOpacity(opacity);
      } else if (layerType === 'vhi') {
        layerManagerRef.current.setVHIPHLayerOpacity(opacity);
      } else if (['maize', 'sugarCane', 'rice', 'coconut'].includes(layerType)) {
        layerManagerRef.current.setCropAreaLayerOpacity(layerType as CropAreaLayerKey, opacity);
      } else if (layerType === 'bsri') {
        layerManagerRef.current.setBSRILayerOpacity(opacity);
      } else       if (layerType === 'riceForecast') {
        layerManagerRef.current.setRiceForecastLayerOpacity(opacity);
      } else if (layerType === 'treeDetection') {
        layerManagerRef.current.setTreeDetectionLayerOpacity(opacity);
      } else if (layerType === 'strawBurn') {
        layerManagerRef.current.setStrawBurnLayerOpacity(opacity);
      } else if (layerType === 'riceGrowthStage') {
        layerManagerRef.current.setRiceGrowthStageLayerOpacity(opacity);
      } else if (layerType === 'irrigationType') {
        layerManagerRef.current.setIrrigationTypeLayerOpacity(opacity);
      } else if (layerType.startsWith('plantingMethod')) {
        const region =
          layerType === 'plantingMethodDry2025'
            ? 'isabela'
            : layerType === 'plantingMethodDry2024'
              ? 'laguna'
              : 'isabela';
        layerManagerRef.current.setPlantingMethodLayerOpacity(region, opacity);
      } else if (layerType === 'cadiDrought') {
        layerManagerRef.current.setCADIDroughtLayerOpacity(opacity);
      } else if (layerType === 'pestRisk') {
        layerManagerRef.current.setPestRiskLayerOpacity(opacity);
      } else if (layerType === 'isabelaPhilriceNDVI' || layerType === 'isabelaPhilriceOrtho') {
        const kind = layerType === 'isabelaPhilriceNDVI' ? ('ndvi' as const) : ('ortho' as const);
        layerManagerRef.current.setIsabelaPhilriceLayerOpacity(kind, opacity);
      } else if (layerType === 'caviteSilangNDVI' || layerType === 'caviteSilangOrtho') {
        const kind = layerType === 'caviteSilangNDVI' ? ('ndvi' as const) : ('ortho' as const);
        layerManagerRef.current.setCaviteSilangLayerOpacity(kind, opacity);
      } else if (layerType === 'heatMap' || layerType === 'timeSeries') {
        layerManagerRef.current.adjustLayerOpacity(layerType, opacity);
      }
    }
  };

  // Handle BSRI time series controls
  const handleBSRIIndexChange = (index: number) => {
    if (!bsriData || !layerManagerRef.current) return;

    setBsriCurrentIndex(index);
    layerManagerRef.current.updateBSRILayer(bsriData, index, layerOpacity.bsri);
  };

  const handleBSRIPlayToggle = () => {
    setBsriIsPlaying(!bsriIsPlaying);
  };

  const handleBSRISpeedChange = (speed: number) => {
    setBsriPlaybackSpeed(speed);
  };

  const handleWMSDateChange = (date: string, layerType: 'yield' | 'vhi' | 'lst') => {
    if (layerManagerRef.current && layerType === 'yield') {
      layerManagerRef.current.updateWMSLayerTime('yield', date);
    }

    if (onWMSDateChange) {
      onWMSDateChange(date, layerType);
    }
  };

  // Get legend URLs and crop area legend info
  const getLegendUrls = () => {
    if (!layerManagerRef.current) return {};

    const legendUrls: Record<string, string> = {
      yield: layerManagerRef.current.getWMSLegendUrl('yield'),
    };

    return legendUrls;
  };

  // Get crop area legend info
  const getCropAreaLegendInfo = () => {
    if (!layerManagerRef.current) return {};

    const cropLegends: Record<string, { colors: string[]; labels: string[]; description: string; totalArea?: number }> = {};

    // Always get legend info for all crop layers (not just visible ones)
    // This ensures all legends are available when layers are toggled on
    const maizeLegend = layerManagerRef.current.getCropAreaLegendInfo('maize');
    if (maizeLegend) cropLegends.maize = maizeLegend;

    const sugarCaneLegend = layerManagerRef.current.getCropAreaLegendInfo('sugarCane');
    if (sugarCaneLegend) cropLegends.sugarCane = sugarCaneLegend;

    const riceLegend = layerManagerRef.current.getCropAreaLegendInfo('rice');
    if (riceLegend) cropLegends.rice = riceLegend;

    const coconutLegend = layerManagerRef.current.getCropAreaLegendInfo('coconut');
    if (coconutLegend) cropLegends.coconut = coconutLegend;

    // Always get rice forecast legend if available
    const forecastLegend = layerManagerRef.current.getRiceForecastLegendInfo();
    if (forecastLegend) {
      cropLegends.riceForecast = forecastLegend;
    }

    // Always get BSRI legend if available
    const bsriLegend = layerManagerRef.current.getBSRILegendInfo();
    if (bsriLegend) {
      cropLegends.bsri = bsriLegend;
    }

    // Always get Straw Burn Detection legend if available
    const strawBurnLegend = layerManagerRef.current.getStrawBurnLegendInfo();
    if (strawBurnLegend) {
      cropLegends.strawBurn = strawBurnLegend;
    }

    return cropLegends;
  };

  // Get current layer visibility state (combining WMS and non-WMS)
  // Use useCallback to ensure it always reads the latest state from ref
  // This ensures UI updates immediately when switching layers, even before React re-renders
  const getCurrentLayerVisibility = useCallback((): LayerVisibilityState => {
    // Read from ref to get the absolute latest state, even if React hasn't re-rendered yet
    const current = layerVisibilityRef.current;
    return {
      yield: current.yield,
      vhi: current.vhi,
      lst: current.lst,
      heatMap: current.heatMap,
      timeSeries: current.timeSeries,
      maize: current.maize,
      sugarCane: current.sugarCane,
      rice: current.rice,
      coconut: current.coconut,
      droneNDVI: current.droneNDVI,
      riceForecast: current.riceForecast,
      bsri: current.bsri,
      strawBurn: current.strawBurn,
      plantingMethodDry2025: current.plantingMethodDry2025,
      plantingMethodDry2024: current.plantingMethodDry2024,
      riceGrowthStage: current.riceGrowthStage,
      irrigationType: current.irrigationType,
      cadiDrought: current.cadiDrought,
      pestRisk: current.pestRisk,
      isabelaPhilriceNDVI: current.isabelaPhilriceNDVI,
      isabelaPhilriceOrtho: current.isabelaPhilriceOrtho,
      caviteSilangNDVI: current.caviteSilangNDVI,
      caviteSilangOrtho: current.caviteSilangOrtho,
      treeDetection: current.treeDetection,
    };
  }, []); // Empty deps - always reads from ref which is kept in sync

  // Manual draw — blue palette; AI delineation reuses these colors for selected field / edit
  const createDrawingStyles = () => {
    return {
      featureStyle: new Style({
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.3)',
        }),
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
        }),
      }),
      editStyle: new Style({
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.15)',
        }),
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
          lineDash: [8, 6],
        }),
      }),
      drawingStyle: new Style({
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.2)',
        }),
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
          lineDash: [10, 10],
        }),
        image: new Circle({
          radius: 6,
          fill: new Fill({
            color: '#3B82F6',
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 2,
          }),
        }),
      }),
      modifyStyle: new Style({
        image: new Circle({
          radius: 8,
          fill: new Fill({
            color: '#FF6B6B',
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 2,
          }),
        }),
      }),
    };
  };

  // Initialize drawing layer and interactions
  const initializeDrawing = () => {
    if (!mapInstanceRef.current) return;

    const styles = createDrawingStyles();

    const drawSource = new VectorSource();
    drawSourceRef.current = drawSource;

    const drawLayer = new VectorLayer({
      source: drawSource,
      style: styles.featureStyle,
      zIndex: 1000,
    });
    drawLayerRef.current = drawLayer;
    mapInstanceRef.current.addLayer(drawLayer);

    const drawInteraction = new Draw({
      source: drawSource,
      type: 'Polygon',
      style: styles.drawingStyle,
    });
    drawInteractionRef.current = drawInteraction;

    const modifyInteraction = new Modify({ 
      source: drawSource,
      style: styles.modifyStyle,
    });
    modifyInteractionRef.current = modifyInteraction;

    const selectInteraction = new Select({
      // Shift+click selects a block so normal clicks keep placing Draw vertices.
      condition: (evt) => click(evt) && !!(evt.originalEvent as MouseEvent | undefined)?.shiftKey,
      layers: (layer) => layer === drawLayer,
      style: new Style({
        fill: new Fill({ color: 'rgba(234, 88, 12, 0.35)' }),
        stroke: new Stroke({ color: '#EA580C', width: 3 }),
        image: new Circle({
          radius: 6,
          fill: new Fill({ color: '#EA580C' }),
          stroke: new Stroke({ color: '#FFFFFF', width: 2 }),
        }),
      }),
    });
    selectInteractionRef.current = selectInteraction;

    const snapInteraction = new Snap({ source: drawSource });
    snapInteractionRef.current = snapInteraction;

    drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      const geometry = feature.getGeometry();

      if (!geometry || geometry.getType() !== 'Polygon') {
        return;
      }

      const ring = geometry.getCoordinates()[0];
      // OpenLayers only finishes a polygon when the ring is closed (first === last); require ≥ 4 points.
      if (!ring || ring.length < 4) {
        return;
      }

      feature.setStyle(styles.featureStyle);

      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.getView().changed();
          mapInstanceRef.current.renderSync();
        }
      }, 100);

      const geoJsonFormat = new GeoJSON();
      const geoJsonFeature = geoJsonFormat.writeFeatureObject(feature, {
        featureProjection: mapInstanceRef.current?.getView().getProjection(),
        dataProjection: 'EPSG:4326',
      });

      if (onDrawPolygonReady) {
        onDrawPolygonReady(geoJsonFeature);
      } else if (onFieldDrawn) {
        onFieldDrawn(geoJsonFeature);
      }
    });

    modifyInteraction.on('modifyend', () => {
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.getView().changed();
          mapInstanceRef.current.renderSync();
        }
      }, 100);
    });
  };

  // Clean up drawing interactions
  const cleanupDrawing = () => {
    if (mapInstanceRef.current) {
      if (drawInteractionRef.current) {
        mapInstanceRef.current.removeInteraction(drawInteractionRef.current);
        drawInteractionRef.current = null;
      }
      if (modifyInteractionRef.current) {
        mapInstanceRef.current.removeInteraction(modifyInteractionRef.current);
        modifyInteractionRef.current = null;
      }
      if (selectInteractionRef.current) {
        mapInstanceRef.current.removeInteraction(selectInteractionRef.current);
        selectInteractionRef.current = null;
      }
      if (snapInteractionRef.current) {
        mapInstanceRef.current.removeInteraction(snapInteractionRef.current);
        snapInteractionRef.current = null;
      }
    }
  };

  // Clear all drawn features
  const clearDrawnFeatures = () => {
    selectInteractionRef.current?.getFeatures().clear();
    if (drawSourceRef.current) {
      drawSourceRef.current.clear();
    }
    // Reset to solid feature style (undo dotted edit style if it was set)
    if (drawLayerRef.current) {
      const styles = createDrawingStyles();
      drawLayerRef.current.setStyle(styles.featureStyle);
    }
  };

  /** Export all drawn blocks as Polygon (1) or MultiPolygon (2+). */
  const writeDrawFeatureGeoJson = useCallback((): Record<string, unknown> | null => {
    const features = drawSourceRef.current?.getFeatures() ?? [];
    if (!features.length || !mapInstanceRef.current) return null;
    const projection = mapInstanceRef.current.getView().getProjection();
    const geoJsonFormat = new GeoJSON();

    const polygonCoords: number[][][][] = [];
    for (const feature of features) {
      const geom = feature.getGeometry();
      if (!geom || geom.getType() !== 'Polygon') continue;
      const cloned = geom.clone();
      cloned.transform(projection, 'EPSG:4326');
      polygonCoords.push((cloned as OlPolygon).getCoordinates());
    }
    if (!polygonCoords.length) return null;

    const geometry =
      polygonCoords.length === 1
        ? { type: 'Polygon', coordinates: polygonCoords[0] }
        : { type: 'MultiPolygon', coordinates: polygonCoords };

    return {
      type: 'Feature',
      properties: { block_count: polygonCoords.length },
      geometry,
    };
  }, []);

  const hasManualDrawPolygon = useCallback(
    () => (drawSourceRef.current?.getFeatures().length ?? 0) > 0,
    []
  );

  const getManualDrawBlockCount = useCallback(
    () => drawSourceRef.current?.getFeatures().length ?? 0,
    []
  );

  const getManualDrawGeoJson = useCallback(
    () => writeDrawFeatureGeoJson(),
    [writeDrawFeatureGeoJson]
  );

  const clearManualDraw = useCallback(() => {
    clearDrawnFeatures();
  }, []);

  const restartManualDraw = useCallback(() => {
    clearDrawnFeatures();
    // Reset to solid style for new drawing
    if (drawLayerRef.current) {
      const styles = createDrawingStyles();
      drawLayerRef.current.setStyle(styles.featureStyle);
    }
    if (!mapInstanceRef.current || !drawInteractionRef.current) return;
    if (!mapInstanceRef.current.getInteractions().getArray().includes(drawInteractionRef.current)) {
      mapInstanceRef.current.addInteraction(drawInteractionRef.current);
    }
    mapInstanceRef.current.getTargetElement().style.cursor = 'crosshair';
  }, []);

  /** Remove the selected block (or the last drawn block if none selected). */
  const removeSelectedManualDrawBlock = useCallback((): boolean => {
    const source = drawSourceRef.current;
    if (!source || source.getFeatures().length === 0) return false;

    const selected = selectInteractionRef.current?.getFeatures();
    let target = selected?.getLength() ? selected.item(0) : null;
    if (!target) {
      const all = source.getFeatures();
      target = all[all.length - 1] ?? null;
    }
    if (!target) return false;

    source.removeFeature(target);
    selected?.clear();
    return true;
  }, []);

  /** Load Polygon / MultiPolygon / FeatureCollection onto the drawing layer (one OL feature per block). */
  const loadGeometryForEdit = useCallback((geometry: any) => {
    if (!mapInstanceRef.current) return;

    const geoJsonFormat = new GeoJSON();

    if (!drawSourceRef.current) {
      initializeDrawing();
    }
    if (!drawSourceRef.current) return;

    drawSourceRef.current.clear();
    selectInteractionRef.current?.getFeatures().clear();

    const styles = createDrawingStyles();
    if (drawLayerRef.current) {
      drawLayerRef.current.setStyle(styles.editStyle);
    }

    const addPolygonGeometry = (geom: { type?: string; coordinates?: unknown }) => {
      if (!geom?.type || !geom.coordinates || !drawSourceRef.current || !mapInstanceRef.current) return;
      if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
        for (const polyCoords of geom.coordinates as number[][][][]) {
          addPolygonGeometry({ type: 'Polygon', coordinates: polyCoords });
        }
        return;
      }
      if (geom.type !== 'Polygon') return;
      const feature = geoJsonFormat.readFeature(
        { type: 'Feature', properties: {}, geometry: geom },
        {
          featureProjection: mapInstanceRef.current.getView().getProjection(),
          dataProjection: 'EPSG:4326',
        }
      );
      if (Array.isArray(feature)) {
        feature.forEach((f) => drawSourceRef.current?.addFeature(f));
      } else {
        drawSourceRef.current.addFeature(feature);
      }
    };

    if (geometry?.type === 'FeatureCollection' && Array.isArray(geometry.features)) {
      for (const f of geometry.features) {
        if (f?.geometry) addPolygonGeometry(f.geometry);
      }
    } else if (geometry?.type === 'Feature' && geometry.geometry) {
      addPolygonGeometry(geometry.geometry);
    } else {
      addPolygonGeometry({
        type: geometry?.type || 'Polygon',
        coordinates: geometry?.coordinates,
      });
    }

    const extent = drawSourceRef.current.getExtent();
    if (extent) {
      mapInstanceRef.current.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 18 });
    }
  }, []);

  const syncDrawingInteractions = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // While drawing mode is on: always allow adding another block (Draw stays active).
    // After the first block closes (manualDrawEditing): also enable Modify + Select.
    const active = isDrawingModeRef.current;
    const hasBlocks = (drawSourceRef.current?.getFeatures().length ?? 0) > 0;
    const editing = active && (manualDrawEditingRef.current || hasBlocks);

    if (!active) {
      if (drawInteractionRef.current) map.removeInteraction(drawInteractionRef.current);
      if (modifyInteractionRef.current) map.removeInteraction(modifyInteractionRef.current);
      if (selectInteractionRef.current) map.removeInteraction(selectInteractionRef.current);
      if (snapInteractionRef.current) map.removeInteraction(snapInteractionRef.current);
      map.getTargetElement().style.cursor = '';
      return;
    }

    if (!drawSourceRef.current) {
      initializeDrawing();
    }

    if (drawInteractionRef.current) {
      if (!map.getInteractions().getArray().includes(drawInteractionRef.current)) {
        map.addInteraction(drawInteractionRef.current);
      }
    }
    if (modifyInteractionRef.current) {
      if (editing) {
        if (!map.getInteractions().getArray().includes(modifyInteractionRef.current)) {
          map.addInteraction(modifyInteractionRef.current);
        }
      } else {
        map.removeInteraction(modifyInteractionRef.current);
      }
    }
    if (selectInteractionRef.current) {
      if (editing) {
        if (!map.getInteractions().getArray().includes(selectInteractionRef.current)) {
          map.addInteraction(selectInteractionRef.current);
        }
      } else {
        map.removeInteraction(selectInteractionRef.current);
      }
    }
    if (snapInteractionRef.current) {
      if (!map.getInteractions().getArray().includes(snapInteractionRef.current)) {
        map.addInteraction(snapInteractionRef.current);
      }
    }

    map.getTargetElement().style.cursor = 'crosshair';
  }, []);

  // Enable/disable drawing mode and vertex-edit phase
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const justEnteredDrawing =
      isDrawingMode && !manualDrawEditing && !prevDrawingModeRef.current;
    prevDrawingModeRef.current = isDrawingMode;

    if (isDrawingMode) {
      if (!drawSourceRef.current) {
        initializeDrawing();
      } else if (justEnteredDrawing && !manualDrawEditing) {
        drawSourceRef.current.clear();
        selectInteractionRef.current?.getFeatures().clear();
      }
    }

    syncDrawingInteractions();
  }, [isDrawingMode, manualDrawEditing, onFieldDrawn, onDrawPolygonReady, syncDrawingInteractions]);

  const droneProductLayersRef = useRef<globalThis.Map<string, BaseLayer>>(new globalThis.Map());
  const droneCameraLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const droneFocusKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;
    const existing = droneProductLayersRef.current;
    const wanted = droneJobOverlay?.layers ?? [];
    const wantedKeys = new Set(wanted.map((layer) => layer.key));
    existing.forEach((layer, key) => {
      if (!wantedKeys.has(key)) {
        map.removeLayer(layer);
        existing.delete(key);
      }
    });
    wanted.forEach((item, index) => {
      let layer = existing.get(item.key);
      if (!layer) {
        layer = item.elevationUrl
          ? new ImageLayer({
              source: new RasterSource({
                sources: [
                  new XYZ({ url: item.tileUrl, crossOrigin: 'anonymous' }),
                  new XYZ({ url: item.elevationUrl, crossOrigin: 'anonymous' }),
                ],
                operation: floodGroundPixels,
                threads: 0,
              }),
            })
          : new TileLayer({ source: new XYZ({ url: item.tileUrl }) });
        layer.set('tileUrl', item.tileUrl);
        map.addLayer(layer);
        existing.set(item.key, layer);
      } else if (!item.elevationUrl && layer instanceof TileLayer && layer.get('tileUrl') !== item.tileUrl) {
        layer.setSource(new XYZ({ url: item.tileUrl }));
        layer.set('tileUrl', item.tileUrl);
      }
      layer.setZIndex(40 + index);
    });

    let cameras = droneCameraLayerRef.current;
    if (!cameras) {
      cameras = new VectorLayer({
        source: new VectorSource(),
        style: new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({ color: '#38bdf8' }),
            stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
          }),
        }),
      });
      map.addLayer(cameras);
      droneCameraLayerRef.current = cameras;
    }
    cameras.setZIndex(40 + wanted.length);
    const cameraSource = cameras.getSource();
    cameraSource?.clear();
    const shots = droneJobOverlay?.shots;
    if (shots?.features?.length && cameraSource) {
      cameraSource.addFeatures(
        new GeoJSON().readFeatures(shots, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
      );
    }

    const focus = droneJobOverlay?.focus;
    if (!focus) {
      droneFocusKeyRef.current = null;
    } else if (focus.key !== droneFocusKeyRef.current && focus.extent.every((value) => Number.isFinite(value))) {
      droneFocusKeyRef.current = focus.key;
      map.getView().fit(transformExtent(focus.extent, 'EPSG:4326', 'EPSG:3857'), {
        padding: [48, 48, 48, 48],
        duration: 400,
        maxZoom: 19,
      });
    }
  }, [droneJobOverlay, mapInstance]);

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;
    return () => {
      droneProductLayersRef.current.forEach((layer) => map.removeLayer(layer));
      droneProductLayersRef.current.clear();
      if (droneCameraLayerRef.current) map.removeLayer(droneCameraLayerRef.current);
      droneCameraLayerRef.current = null;
      droneFocusKeyRef.current = null;
    };
  }, [mapInstance]);

  // Initialize the map
  useEffect(() => {
    if (!mapRef.current) return;

    const map = new Map({
      target: mapRef.current,
      layers: [],
      view: new View({
        center: fromLonLat(mapCoordinates),
        zoom: zoomLevel,
      }),
      pixelRatio: getMapPixelRatio(),
      controls: defaultControls({ zoom: false, attribution: false }),
    });

    mapInstanceRef.current = map;
    setMapInstance(map);
    
    // Initialize layer manager
    layerManagerRef.current = new MapLayerManager(map);
    layerManagerRef.current.setDroneSiteLayerListener(setDroneSiteActiveLayerId);
    
    // Expose debug methods to global scope for troubleshooting
    (window as any).debugMapLayers = {
      debugWMSLayers: () => layerManagerRef.current?.debugWMSLayers(),
      testYieldLayer: () => layerManagerRef.current?.testYieldLayer(),
      fixYieldLayer: () => layerManagerRef.current?.fixYieldLayer(),
      refreshAllWMSLayers: () => layerManagerRef.current?.refreshAllVisibleWMSLayers()
    };
    
    // Add base layer
    const baseLayers = MapLayerManager.createBaseLayers();
    map.addLayer(baseLayers[currentBaseLayer]);
    
    // Initialize vector layer for field boundaries
    layerManagerRef.current.initializeVectorLayer();

    const searchMarkerSource = new VectorSource();
    const searchMarkerLayer = new VectorLayer({
      source: searchMarkerSource,
      style: new Style({
        image: new Circle({
          radius: 8,
          fill: new Fill({
            color: '#10b981', // emerald-500
          }),
          stroke: new Stroke({
            color: '#ffffff',
            width: 3,
          }),
        }),
      }),
      zIndex: 1000, // Ensure marker appears on top
    });
    map.addLayer(searchMarkerLayer);
    searchMarkerSourceRef.current = searchMarkerSource;
    searchMarkerLayerRef.current = searchMarkerLayer;

    // Set up map event listeners
    const isCurrentLocationLayer = (layer: { get: (k: string) => unknown }) =>
      layer.get('ignoreClick') === true || layer.get('layerType') === 'currentLocation';

    map.on('click', (event) => {
      if (isDrawingMode) return;

      // Location marker / accuracy circle: visible only — never open farm analysis
      const onLocationOverlay = map.forEachFeatureAtPixel(event.pixel, () => true, {
        layerFilter: isCurrentLocationLayer,
      });
      if (onLocationOverlay) return;

      // During AI delineation picking, never fall through to saved-farm clicks (that clears blocks).
      if (isAiDelineationPickingRef.current) {
        const delineationLayer = layerManagerRef.current?.getCropfieldDelineationLayer();
        if (delineationLayer?.getVisible()) {
          const delineationFeature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, {
            layerFilter: (layer) => layer === delineationLayer,
          });
          if (delineationFeature && onDelineationFieldClickRef.current) {
            const idx = Number(delineationFeature.get('delineation_index'));
            if (Number.isFinite(idx)) {
              const oe = event.originalEvent as MouseEvent | undefined;
              onDelineationFieldClickRef.current(idx, {
                ctrlKey: oe?.ctrlKey,
                metaKey: oe?.metaKey,
                shiftKey: oe?.shiftKey,
              });
              return;
            }
          }
        }
        if (onDelineationClearSelectionRef.current) {
          onDelineationClearSelectionRef.current();
        }
        return;
      }
      
      const overviewLayers = layerManagerRef.current?.getFarmOverviewLayers?.() ?? [];
      const overviewLayerSet = new Set(overviewLayers);
      const feature = map.forEachFeatureAtPixel(
        event.pixel,
        (feat) => feat,
        {
          layerFilter: (layer) => {
            if (isCurrentLocationLayer(layer)) return false;
            // Prefer labeled farm overview / cluster pins when present
            if (overviewLayerSet.size > 0 && overviewLayerSet.has(layer as any)) return true;
            return !overviewLayerSet.has(layer as any);
          },
        }
      );
      // Prefer overview / cluster hit when those layers are visible
      let hit = feature;
      if (overviewLayers.some((layer) => layer.getVisible())) {
        const overviewHit = map.forEachFeatureAtPixel(event.pixel, (feat) => feat, {
          layerFilter: (layer) => overviewLayerSet.has(layer as any),
        });
        if (overviewHit) hit = overviewHit;
      }
      if (hit && onFieldClickRef.current) {
        // Multi-farm cluster bubble → zoom in instead of opening one farm
        if (layerManagerRef.current?.zoomIntoFarmCluster?.(hit as any)) {
          return;
        }
        const properties = hit.getProperties() || {};
        // Cluster of one: unwrap the member farm properties
        const members = properties.features;
        const farmProps =
          Array.isArray(members) && members.length === 1
            ? (members[0] as { getProperties?: () => Record<string, unknown> }).getProperties?.() ||
              properties
            : properties;
        const { geometry: _geom, features: _features, ...rest } = farmProps as Record<
          string,
          unknown
        >;
        onFieldClickRef.current(rest);
      }
      
      const coordinates = toLonLat(event.coordinate);
      if (!hit) {
        inspectCadiPixelRef.current(coordinates[0], coordinates[1], event.pixel);
      }
    });

    const handleViewportContextMenu = (domEvent: MouseEvent) => {
      domEvent.preventDefault();
      if (isDrawingModeRef.current) return;
      const vl = layerManagerRef.current?.getVectorLayer();
      const handler = onFieldContextMenuRef.current;
      if (!vl || !handler) return;
      const pixel = map.getEventPixel(domEvent);
      const feature = map.forEachFeatureAtPixel(pixel, (f) => f, {
        layerFilter: (layer) => layer === vl,
      });
      if (feature) {
        handler(feature.getProperties() as Record<string, unknown>);
      }
    };
    map.getViewport().addEventListener('contextmenu', handleViewportContextMenu);

    map.on('pointermove', (event) => {
      if (isDrawingMode) return;

      // Don't show pointer cursor over location overlay
      const onLocationOverlay = map.forEachFeatureAtPixel(event.pixel, () => true, {
        layerFilter: isCurrentLocationLayer,
      });
      if (onLocationOverlay) {
        map.getTargetElement().style.cursor = '';
        return;
      }

      let cursor = '';
      if (isAiDelineationPickingRef.current) {
        const delineationLayer = layerManagerRef.current?.getCropfieldDelineationLayer();
        if (delineationLayer?.getVisible()) {
          const delineationFeature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, {
            layerFilter: (layer) => layer === delineationLayer,
          });
          if (delineationFeature) cursor = 'pointer';
        }
      }
      if (!cursor) {
        const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, {
          layerFilter: (layer) => !isCurrentLocationLayer(layer),
        });
        cursor = feature ? 'pointer' : '';
      }
      if (!cursor && rasterInspectOnRef.current) cursor = 'pointer';
      map.getTargetElement().style.cursor = cursor;
    });

    map.getView().on('change:center', () => {
      const center = toLonLat(map.getView().getCenter() || [0, 0]);
      setMapCoordinates([center[0], center[1]]);
    });

    map.getView().on('change:resolution', () => {
      setZoomLevel(Math.round(map.getView().getZoom() || 16));
    });

    // Cleanup function
    return () => {
      map.getViewport().removeEventListener('contextmenu', handleViewportContextMenu);
      cleanupDrawing();
      if (searchMarkerSourceRef.current) {
        searchMarkerSourceRef.current.clear();
        searchMarkerSourceRef.current = null;
      }
      if (searchMarkerLayerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(searchMarkerLayerRef.current);
        searchMarkerLayerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined);
        mapInstanceRef.current = null;
      }
      if (layerManagerRef.current) {
        layerManagerRef.current.cleanup();
        layerManagerRef.current = null;
      }
    };
  }, []);

  // Keep map size in sync when panels resize or presentation zoom changes.
  useEffect(() => {
    const el = mapRef.current;
    const map = mapInstanceRef.current;
    if (!el || !map) return;

    const ro = new ResizeObserver(() => {
      map.updateSize();
    });
    ro.observe(el);

    const onDisplayScaleChange = () => {
      map.updateSize();
      layerManagerRef.current?.getCropfieldDelineationLayer()?.changed();
      map.render();
    };
    window.addEventListener(DISPLAY_SCALE_CHANGE_EVENT, onDisplayScaleChange);

    return () => {
      ro.disconnect();
      window.removeEventListener(DISPLAY_SCALE_CHANGE_EVENT, onDisplayScaleChange);
    };
  }, [mapInstance]);

  // Update base layer when changed
  useEffect(() => {
    if (layerManagerRef.current) {
      layerManagerRef.current.changeBaseLayer(currentBaseLayer);
    }
  }, [currentBaseLayer]);

  // Update GeoJSON visualization when fieldGeoJson changes
  useEffect(() => {
    if (layerManagerRef.current) {
      // Handle both regular fields and demo fields
      let geoJsonToDisplay = fieldGeoJson;

      // If selectedField has geometry but fieldGeoJson is null (edge case)
      if (!fieldGeoJson && selectedField?.geometry) {
        geoJsonToDisplay = selectedField.geometry;
      }

      // If selectedField has _geometry (from FieldsPanel formatting)
      if (!fieldGeoJson && selectedField?._geometry) {
        geoJsonToDisplay = selectedField._geometry;
      }

      layerManagerRef.current.updateGeoJsonLayer(geoJsonToDisplay, selectedField);
    }

    // Clear drawn features when fieldGeoJson is removed (e.g., when deleting a demo field)
    if (!fieldGeoJson && !selectedField) {
      clearDrawnFeatures();
    }
  }, [fieldGeoJson, selectedField]);

  // Opened farm group / all-farms overview: polygons with farm · block labels
  useEffect(() => {
    if (!mapInstance || !layerManagerRef.current) return;
    layerManagerRef.current.updateFarmGroupLayer(farmGroupGeoJson, { fit: farmGroupFit });
  }, [mapInstance, farmGroupGeoJson, farmGroupFit]);

  // LGU monitoring-area boundary outline
  useEffect(() => {
    if (!mapInstance || !layerManagerRef.current) return;
    layerManagerRef.current.updateMonitoringAreaBoundaryLayer(monitoringAreaBoundaryGeoJson);
  }, [mapInstance, monitoringAreaBoundaryGeoJson]);

  // Handle heat map data
  useEffect(() => {
    if (layerManagerRef.current) {
      layerManagerRef.current.updateHeatMapLayer(indexHeatMapData, layerOpacity.heatMap);
      if (indexHeatMapData?.tileUrl) {
        setLayerVisibility(prev => ({ ...prev, heatMap: true }));
        showLegendPanel();
      } else {
        setLayerVisibility(prev => ({ ...prev, heatMap: false }));
      }
    }
  }, [indexHeatMapData, layerOpacity.heatMap]);

  // Handle time series data. While change detection is active the split layers own the map.
  useEffect(() => {
    if (isChangeDetectionMode && changeDetectionImages.before?.tile_url && changeDetectionImages.after?.tile_url) {
      if (layerManagerRef.current) {
        layerManagerRef.current.updateTimeSeriesLayer(null);
      }
      return;
    }
    if (timeSeriesData?.results && timeSeriesData.results.length > 0) {
      showLegendPanel();
      if (layerManagerRef.current) {
        const frame = timeSeriesData.results[currentTimeSeriesIndex] ?? timeSeriesData.results[0];
        layerManagerRef.current.updateTimeSeriesLayer(
          frame,
          layerOpacity.timeSeries
        );
        setLayerVisibility(prev => ({ ...prev, timeSeries: true }));
      }
    } else {
      if (layerManagerRef.current) {
        layerManagerRef.current.updateTimeSeriesLayer(null);
        setLayerVisibility(prev => ({ ...prev, timeSeries: false }));
      }
    }
  }, [timeSeriesData, layerOpacity.timeSeries, isChangeDetectionMode, changeDetectionImages, currentTimeSeriesIndex]);

  // Handle opacity adjustments from AnalysisPanel
  useEffect(() => {
    if (analysisResults?.type === 'adjust-heatmap-opacity') {
      handleOpacityChange('heatMap', analysisResults.opacity);
    } else if (analysisResults?.type === 'adjust-timeseries-opacity') {
      handleOpacityChange('timeSeries', analysisResults.opacity);
    }
  }, [analysisResults]);

  // Handle rice forecast data updates
  useEffect(() => {
    if (layerManagerRef.current && riceForecastData) {
      layerManagerRef.current.updateRiceForecastLayer(riceForecastData, layerOpacity.riceForecast);
    }
  }, [riceForecastData, layerOpacity.riceForecast]);

  // Tree detection is not displayed on map - only in layer panel
  // Removed useEffect that adds tree detection layer to map

  // Handle BSRI autoplay
  useEffect(() => {
    if (bsriIsPlaying && bsriData && bsriData.tiles && bsriData.tiles.length > 0) {
      bsriIntervalRef.current = setInterval(() => {
        setBsriCurrentIndex(prev => {
          const nextIndex = (prev + 1) % bsriData.tiles.length;
          if (layerManagerRef.current) {
            layerManagerRef.current.updateBSRILayer(bsriData, nextIndex, layerOpacity.bsri);
          }
          return nextIndex;
        });
      }, bsriPlaybackSpeed);
    } else if (bsriIntervalRef.current) {
      clearInterval(bsriIntervalRef.current);
      bsriIntervalRef.current = null;
    }

    return () => {
      if (bsriIntervalRef.current) {
        clearInterval(bsriIntervalRef.current);
        bsriIntervalRef.current = null;
      }
    };
  }, [bsriIsPlaying, bsriPlaybackSpeed, bsriData, layerOpacity.bsri]);

  // Handle search location selection
  const handleLocationSelect = (lat: number, lon: number, name: string) => {
    if (!mapInstanceRef.current || !searchMarkerSourceRef.current) return;

    searchMarkerSourceRef.current.clear();

    const coordinate = fromLonLat([lon, lat]);
    const markerFeature = new Feature({
      geometry: new Point(coordinate),
      name: name,
    });
    searchMarkerSourceRef.current.addFeature(markerFeature);

    mapInstanceRef.current.getView().animate({
      center: coordinate,
      zoom: 15,
      duration: 1000,
    });
  };

  // Handle zoom to field
  const handleZoomToField = () => {
    if (layerManagerRef.current && selectedField) {
      layerManagerRef.current.zoomToField();
    }
  };

  // Get current analysis info
  const analysisInfo = getCurrentAnalysisInfo(
    showTimeSeriesSlider,
    getCurrentTimeSeriesImage(),
    timeSeriesData,
    indexHeatMapData
  );

  // Get visualization parameters
  const visParams = getVisualizationParams(timeSeriesData, indexHeatMapData);

  // Active time-series layer for slider (Past Yield WMS, VHI/LST Philippines GEE tiles, BSRI)
  const getActiveWMSLayer = (): { type: 'yield' | 'vhi' | 'lst' | 'bsri' | 'cadi' | 'pestRisk'; name: string } | null => {
    if (wmsLayerStates?.yield?.visible) return { type: 'yield', name: 'Yield' };

    if (layerVisibility.cadiDrought) {
      return { type: 'cadi', name: 'El Niño CADI Agricultural Drought' };
    }

    if (layerVisibility.pestRisk) {
      return {
        type: 'pestRisk',
        name: `${getPestRiskPestLabel(pestRiskPestKey)} · Pest Occurrence Probability Index`,
      };
    }

    if (vhiPHData && vhiPHData.tiles && vhiPHData.tiles.length > 0) {
      return { type: 'vhi', name: 'VHI Philippines' };
    }
    if (lstPHData && lstPHData.tiles && lstPHData.tiles.length > 0) {
      return { type: 'lst', name: 'LST Philippines' };
    }

    if (wmsLayerStates?.vhi?.visible) return { type: 'vhi', name: 'VHI' };
    if (wmsLayerStates?.lst?.visible) return { type: 'lst', name: 'LST' };

    if (layerVisibility.bsri) return { type: 'bsri', name: 'Black Sigatoka Risk Index' };

    return null;
  };

  // GeoServer WMS layers visible on map (Past Yield only)
  const getVisibleWMSLayersCount = (): number => {
    if (!wmsLayerStates) return 0;
    return wmsLayerStates.yield?.visible ? 1 : 0;
  };

  const getPlayingWMSLayersCount = (): number => {
    if (!wmsLayerStates) return 0;
    return wmsLayerStates.yield?.isPlaying ? 1 : 0;
  };
  const getLSTPHDateArray = (): string[] => {
    if (!lstPHData || !lstPHData.tiles) return [];
    return lstPHData.tiles.map((tile: any) => tile.date);
  };

  const getCADIDateArray = (): string[] => {
    return CADI_DROUGHT_FRAMES.map((frame) => frame.date);
  };

  const getCADIDateLabels = (): string[] => {
    return CADI_DROUGHT_FRAMES.map((frame) => frame.label);
  };

  const getPestRiskDateArray = (): string[] => {
    return getPestRiskFrames(pestRiskPestKey).map((frame) => frame.date);
  };

  const getPestRiskDateLabels = (): string[] => {
    return getPestRiskFrames(pestRiskPestKey).map((frame) => frame.label);
  };

  const handleCADIIndexChange = (index: number) => {
    if (!layerManagerRef.current || !layerVisibility.cadiDrought) return;
    setCadiCurrentIndex(index);
    layerManagerRef.current.updateCADIDroughtLayer(index, layerOpacity.cadiDrought);
  };

  const handlePestRiskIndexChange = (index: number) => {
    if (!layerVisibility.pestRisk) return;
    setPestRiskQuarterIndex(index);
  };

  const handlePestRiskPestChange = (pestKey: PestRiskPestKey) => {
    setPestRiskPestKey(pestKey);
    setPestRiskQuarterIndex(0);
  };

  const handleRiceGrowthStagePeriodChange = (id: RiceGrowthStagePeriodId) => {
    riceGrowthStagePeriodRef.current = id;
    setRiceGrowthStagePeriod(id);
    if (!layerVisibilityRef.current.riceGrowthStage || !layerManagerRef.current) return;
    const period = getRiceGrowthStagePeriod(id);
    layerManagerRef.current.setRiceGrowthStageLoadingListener(setRiceGrowthStageLoading);
    layerManagerRef.current.toggleRiceGrowthStageLayer(
      true,
      layerOpacity.riceGrowthStage,
      period.cogUrl,
      period.bbox
    );
  };

  // Get VHI Philippines date array for slider
  const getVHIPHDateArray = (): string[] => {
    if (!vhiPHData || !vhiPHData.tiles) return [];
    return vhiPHData.tiles.map((tile: any) => tile.date);
  };

  // Get BSRI date array for slider
  const getBSRIDateArray = (): string[] => {
    if (!bsriData || !bsriData.tiles) return [];
    return bsriData.tiles.map((tile: any) => tile.date);
  };

  // Fly map to a lon/lat (e.g. crown segmentation TIF centroid)
  const flyToLocation = (lonLat: [number, number], zoom?: number) => {
    if (!mapInstanceRef.current) return;
    const view = mapInstanceRef.current.getView();
    const targetZoom = zoom ?? view.getZoom() ?? 10;
    view.animate({
      center: fromLonLat(lonLat),
      zoom: targetZoom,
      duration: 1000
    });
  };

  // Handle BSRI date change
  const handleBSRIDateChange = (date: string, layerType: any) => {
    if (layerType !== 'bsri') return;

    // Find the index for this date
    if (bsriData && bsriData.tiles) {
      const frameIndex = bsriData.tiles.findIndex((tile: any) => tile.date === date);

      if (frameIndex !== -1) {
        handleBSRIIndexChange(frameIndex);
      }
    }
  };

  // Handle LST Philippines date change (WMSTimeSeriesSlider when LST is active)
  const handleLSTPHDateChange = (date: string, layerType: any) => {
    if (layerType !== 'lst') return;

    if (lstPHData && lstPHData.tiles && layerManagerRef.current) {
      const frameIndex = lstPHData.tiles.findIndex((tile: any) => tile.date === date);
      if (frameIndex !== -1) {
        setLstPHCurrentIndex(frameIndex);
        layerManagerRef.current.updateLSTPHLayer(lstPHData, frameIndex, layerOpacity.lst);
      }
    }
  };

  // Handle VHI Philippines date change (WMSTimeSeriesSlider when VHI is active)
  const handleVHIPHDateChange = (date: string, layerType: any) => {
    if (layerType !== 'vhi') return;

    if (vhiPHData && vhiPHData.tiles && layerManagerRef.current) {
      const frameIndex = vhiPHData.tiles.findIndex((tile: any) => tile.date === date);
      if (frameIndex !== -1) {
        setVhiPHCurrentIndex(frameIndex);
        layerManagerRef.current.updateVHIPHLayer(vhiPHData, frameIndex, layerOpacity.vhi);
      }
    }
  };

  const activeWMSLayer = getActiveWMSLayer();
  const visibleWMSLayersCount = getVisibleWMSLayersCount();
  const playingWMSLayersCount = getPlayingWMSLayersCount();
  
  // Get current layer visibility - use ref for latest value, fallback to state for initial render
  // This ensures we always have the most up-to-date state, even before React re-renders
  const currentLayerVisibility = layerVisibilityRef.current || layerVisibility;
  
  // Create a string key representing WMS layer visibility to trigger parent updates
  // Compute from current state to ensure it updates immediately when state changes
  // This ensures the parent component re-renders when layer visibility changes
  const wmsLayerVisibilityKey = useMemo(() => {
    // Use state directly since useMemo dependencies track state changes
    return `${layerVisibility.yield ? 'y' : 'n'}${layerVisibility.vhi ? 'v' : 'n'}${layerVisibility.lst ? 'l' : 'n'}`;
  }, [layerVisibility.yield, layerVisibility.vhi, layerVisibility.lst]);

  // Expose layer control functions to parent via callback
  useEffect(() => {
    if (onLayerControlReady) {
      onLayerControlReady({
        getCurrentLayerVisibility,
        // Use React state (not ref) so parents re-sync when Isabela / other toggles update visibility
        layerVisibility,
        layerOpacity,
        handleLayerToggle,
        handleOpacityChange,
        droneSiteActiveLayerId,
        droneSiteOpacity,
        handleDroneSiteLayerToggle: stableDroneSiteToggle,
        handleDroneSiteOpacityChange: stableDroneSiteOpacityChange,
        clearDroneSiteLayers,
        getLegendUrls,
        getCropAreaLegendInfo,
        cropAreaLoadingLayers,
        riceGrowthStageLoading,
        irrigationTypeLoading,
        handleForecastDataUpdate,
        handleFloodLayerShow,
        showFloodDamageRaster: (raster, options) => {
          layerManagerRef.current?.showFloodDamageRaster(raster, options);
          const layerOn = !!raster?.url && options?.showLayer !== false;
          setFloodDamageRasterActive(layerOn);
          if (!layerOn) setShowFloodDamageLegendInfo(false);
        },
        handleBSRIDateRangeUpdate,
        bsriIsLoading,
        handleLSTPHDateRangeUpdate,
        lstPHIsLoading,
        handleVHIPHDateRangeUpdate,
        vhiPHIsLoading,
        clearMultiTemporalAPITiles,
        pestRiskPestKey,
        handlePestRiskPestChange,
        riceGrowthStagePeriod,
        handleRiceGrowthStagePeriodChange,
        handleStrawBurnDataFetch,
        handleStrawBurnDataClear,
        strawBurnIsLoading,
        strawBurnData,
        handleTreeDetectionDataUpdate,
        handleCropfieldDelineationUpdate,
        handleCrownSegmentationMapUpdate,
        setCropfieldDelineationSelectedIndex,
        setCropfieldDelineationCheckedIndexes,
        setCropfieldDelineationMultiSelectMode,
        startCropfieldVertexEdit,
        cancelCropfieldVertexEdit,
        undoCropfieldVertexEdit,
        redoCropfieldVertexEdit,
        canUndoCropfieldVertexEdit,
        canRedoCropfieldVertexEdit,
        setCropfieldVertexHistoryListener,
        confirmCropfieldVertexEdit,
        applyCropfieldVertexEditInPlace,
        confirmCropfieldDelineationFeature,
        confirmCropfieldDelineationFeatures,
        getCropfieldDelineationCount,
        getCropfieldDelineationFeatureCollectionGeoJson,
        removeCropfieldDelineationFeatureAt,
        removeCropfieldDelineationFeaturesAt,
        isCropfieldVertexEditActive,
        hasManualDrawPolygon,
        getManualDrawBlockCount,
        getManualDrawGeoJson,
        clearManualDraw,
        restartManualDraw,
        removeSelectedManualDrawBlock,
        loadGeometryForEdit,
        flyToLocation,
        selectSearchLocation: handleLocationSelect,
        mapCoordinates,
        zoomLevel,
        getOlMap: () => mapInstanceRef.current,
        requestDronePrescriptionStats,
        clearDronePrescriptionStats,
        showDroneOrthoIndexMap,
        clearDroneOrthoIndexMap,
        requestMultitemporalProvinceStats,
        selectMultitemporalMunicipality,
        clearMultitemporalZonalStats,
      });
    }
  }, [
    layerOpacity,
    cropAreaLoadingLayers,
    bsriIsLoading,
    lstPHIsLoading,
    vhiPHIsLoading,
    strawBurnIsLoading,
    strawBurnData,
    mapCoordinates,
    zoomLevel,
    wmsLayerVisibilityKey, // Add this to trigger updates when WMS layer visibility changes
    layerVisibility.yield, // Include individual WMS layer states to trigger updates
    layerVisibility.vhi,
    layerVisibility.lst,
    layerVisibility.plantingMethodDry2025, // Planting method layers need to trigger panel refresh
    layerVisibility.plantingMethodDry2024,
    layerVisibility.riceGrowthStage,
    riceGrowthStageLoading,
    riceGrowthStagePeriod,
    layerVisibility.irrigationType,
    irrigationTypeLoading,
    layerVisibility.isabelaPhilriceNDVI,
    layerVisibility.isabelaPhilriceOrtho,
    layerVisibility.caviteSilangNDVI,
    layerVisibility.caviteSilangOrtho,
    droneSiteActiveLayerId,
    droneSiteOpacity,
    stableDroneSiteToggle,
    stableDroneSiteOpacityChange,
    clearDroneSiteLayers,
    layerVisibility.pestRisk,
    layerVisibility.cadiDrought,
    layerVisibility.maize,
    layerVisibility.sugarCane,
    layerVisibility.rice,
    layerVisibility.coconut,
    pestRiskPestKey,
    requestDronePrescriptionStats,
    clearDronePrescriptionStats,
    showDroneOrthoIndexMap,
    clearDroneOrthoIndexMap,
    requestMultitemporalProvinceStats,
    selectMultitemporalMunicipality,
    clearMultitemporalZonalStats,
    getCurrentLayerVisibility, // Include to ensure parent gets updated function reference
    onLayerControlReady
  ]);

  return (
    <div className={`relative w-full h-full bg-gray-900 overflow-hidden ${className}`}>
      {/* Map Container */}
      <div ref={mapRef} className="w-full h-full"></div>
      {cadiPixelReadout && (
        <div
          className={`pointer-events-auto absolute z-20 w-44 rounded-[14px] p-2.5 shadow-lg ${
            isDark ? 'bg-[rgba(22,27,34,.96)] text-[#e6edf3]' : 'bg-[rgba(248,245,236,.98)] text-[#111827]'
          }`}
          style={{
            left: Math.max(8, cadiPixelReadout.x + 12),
            top: Math.max(8, cadiPixelReadout.y - 72),
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <p className={`text-[10px] font-medium ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
              {cadiPixelReadout.title}
            </p>
            <button
              type="button"
              aria-label="Close drought value"
              className="leading-none opacity-60 hover:opacity-100"
              onClick={() => setCadiPixelReadout(null)}
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          {cadiPixelReadout.loading ? (
            <p className="mt-1 text-xs">Reading pixel…</p>
          ) : cadiPixelReadout.value == null ? (
            <p className="mt-1 text-xs">{cadiPixelReadout.error ?? cadiPixelReadout.emptyMessage}</p>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <span
                className="h-3 w-3 shrink-0 rounded-sm border border-black/10"
                style={{ backgroundColor: cadiPixelReadout.color }}
              />
              <div>
                <p className="text-sm font-semibold leading-tight">{cadiPixelReadout.label}</p>
                {cadiPixelReadout.detail && (
                  <p className={`text-[11px] tabular-nums ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
                    {cadiPixelReadout.detail}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Layer Toggle Panel hamburger removed — replaced by the always-visible `MapToolRail`
          rendered by `HomePage` over the map. The rail drives the inline-card `AnalysisPanel`. */}




      {/* Map Controls */}
      <MapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onGetCurrentLocation={getCurrentLocation}
        onZoomToField={handleZoomToField}
        onToggleFullscreen={toggleFullscreen}
        onToggleHeatMap={() => handleLayerToggle('heatMap', !layerVisibility.heatMap)}
        onToggleTimeSeries={() => handleLayerToggle('timeSeries', !layerVisibility.timeSeries)}
        selectedField={selectedField}
        hasIndexHeatMap={!!indexHeatMapData}
        hasTimeSeries={showTimeSeriesSlider}
        isFullscreen={isFullscreen}
        currentBaseLayer={currentBaseLayer}
        onBaseLayerChange={handleBaseLayerChange}
        showBaseLayerMenu={showBaseLayerMenu}
        onToggleBaseLayerMenu={toggleBaseLayerMenu}
      />

      {/* Old place/coordinate `SearchBar` removed — the top-right header search in `HomePage`
          (which filters the field list) is the only search now. */}

      {/* WMS Time Series Slider - Hide in change detection mode */}
      {activeWMSLayer && !isChangeDetectionMode && (
        <WMSTimeSeriesSlider
          dateArray={
            activeWMSLayer.type === 'bsri'
              ? getBSRIDateArray()
              : activeWMSLayer.type === 'cadi'
                ? getCADIDateArray()
              : activeWMSLayer.type === 'pestRisk'
                ? getPestRiskDateArray()
              : activeWMSLayer.type === 'lst' && lstPHData
                ? getLSTPHDateArray()
                : activeWMSLayer.type === 'vhi' && vhiPHData
                  ? getVHIPHDateArray()
                  : (wmsDateArrays?.[activeWMSLayer.type as 'yield' | 'vhi' | 'lst'] || [])
          }
          dateLabels={
            activeWMSLayer.type === 'cadi'
              ? getCADIDateLabels()
              : activeWMSLayer.type === 'pestRisk'
                ? getPestRiskDateLabels()
                : undefined
          }
          layerType={activeWMSLayer.type as any}
          layerName={activeWMSLayer.name}
          isVisible={true}
          onDateChange={
            activeWMSLayer.type === 'bsri'
              ? handleBSRIDateChange
              : activeWMSLayer.type === 'lst' && lstPHData
                ? handleLSTPHDateChange
                : activeWMSLayer.type === 'vhi' && vhiPHData
                  ? handleVHIPHDateChange
                  : handleWMSDateChange
          }
          onPlayToggle={
            activeWMSLayer.type !== 'bsri' && activeWMSLayer.type !== 'cadi' && activeWMSLayer.type !== 'pestRisk'
              ? onWMSPlayToggle
              : undefined
          }
          bsriData={activeWMSLayer.type === 'bsri' ? bsriData : undefined}
          currentIndex={
            activeWMSLayer.type === 'bsri'
              ? bsriCurrentIndex
              : activeWMSLayer.type === 'cadi'
                ? cadiCurrentIndex
              : activeWMSLayer.type === 'pestRisk'
                ? pestRiskQuarterIndex
                : undefined
          }
          onIndexChange={
            activeWMSLayer.type === 'bsri'
              ? handleBSRIIndexChange
              : activeWMSLayer.type === 'cadi'
                ? handleCADIIndexChange
              : activeWMSLayer.type === 'pestRisk'
                ? handlePestRiskIndexChange
                : undefined
          }
        />
      )}

      {/* Enhanced Time Series Slider with Change Detection */}
      {showTimeSeriesSlider && currentTimeSeriesData && !activeWMSLayer && !heatMapObservationBar && (
        <TimeSeriesSlider
          timeSeriesData={currentTimeSeriesData}
          currentIndex={currentTimeSeriesIndex}
          isPlaying={isPlayingTimeSeries}
          playbackSpeed={playbackSpeed}
          onIndexChange={handleTimeSeriesSliderChange}
          onPlayToggle={togglePlayPause}
          onSpeedChange={handleSpeedChange}
          onChangeDetectionToggle={handleChangeDetectionToggle}
        />
      )}

      {changePairReady && (
        <MapChangeSwipe
          onChange={handleComparisonSliderChange}
          mapElement={mapRef.current}
        />
      )}

      {/* Farm Index Map walk mode — live GPS + vegetation/moisture Q&A */}
      {indexWalkAvailable && !activeWMSLayer && (
        <FarmIndexWalkOverlay
          available={indexWalkAvailable}
          farmGeometry={fieldGeoJson}
          farmId={selectedField?.farm_id}
          farmName={selectedField?.farm_name}
          observationDate={
            indexHeatMapData?.observationDate ||
            indexHeatMapData?.first_image_date ||
            indexHeatMapData?.firstImageDate ||
            ''
          }
          index={String(indexHeatMapData?.index || 'ndvi')}
          satellite={String(indexHeatMapData?.satellite || 'S2')}
          indexMapMeta={{
            tileUrl: indexHeatMapData?.tileUrl || indexHeatMapData?.tile_url,
            firstImageDate:
              indexHeatMapData?.first_image_date || indexHeatMapData?.firstImageDate,
            visParams: indexHeatMapData?.visParams || indexHeatMapData?.vis_params,
            spatialResolution:
              indexHeatMapData?.spatialResolution || indexHeatMapData?.spatial_resolution,
            spatialResolutionM:
              indexHeatMapData?.spatialResolutionM ??
              indexHeatMapData?.spatial_resolution_m ??
              null,
          }}
          walkControls={walkControls}
          onWalkingChange={setIndexWalkActive}
          onRegisterActions={(actions) => {
            indexWalkActionsRef.current = actions;
          }}
          onReportReady={(report) => onFarmWalkReport?.(report)}
        />
      )}

      {/* Farm heat map — observation date cards (bottom bar, UI-first) */}
      {heatMapObservationBar && !activeWMSLayer && (
        <HeatMapObservationBar
          {...heatMapObservationBar}
          walkAvailable={indexWalkAvailable}
          isWalking={indexWalkActive}
          onWalkInfo={() => indexWalkActionsRef.current?.showInfo()}
          onWalkToggle={() => {
            const actions = indexWalkActionsRef.current;
            if (!actions) return;
            if (indexWalkActive) actions.stop();
            else actions.start();
          }}
        />
      )}

      {/* Legend - analysis mode (time series index scale, Burn Detection, etc.) — keep visible in Compare mode */}
      {showLegend && (analysisInfo || visParams) && (
        <MapLegend
          analysisInfo={analysisInfo}
          visParams={visParams}
        />
      )}

      {/* Legend - same position as LST / Past Yield: when a WMS or BSRI time-series layer is active.
          Bottom-left of the map, matching MapLegend. */}
      {activeWMSLayer && !isChangeDetectionMode && (activeWMSLayer.type === 'yield' || activeWMSLayer.type === 'vhi' || activeWMSLayer.type === 'lst' || activeWMSLayer.type === 'bsri') && (
        <div
          className={`${lg.anchor} ${lg.shell}`}
        >
          <h5 className={`${lg.title} mb-2`}>{activeWMSLayer.name}</h5>
          {activeWMSLayer.type === 'yield' && (
            <img
              src={getLegendUrls().yield}
              alt={`${activeWMSLayer.name} Legend`}
              className={lg.imgBorder}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
          {/* LST Philippines: use gradient legend from API vis_params, similar to BSRI */}
          {activeWMSLayer.type === 'lst' && lstPHData && lstPHData.tiles && layerManagerRef.current && (() => {
            const lstLegend = layerManagerRef.current.getLSTPHLegendInfo() as { colors: string[]; labels: string[]; min?: number; max?: number; description?: string } | null;
            if (!lstLegend || !lstLegend.colors?.length) return null;
            const min = typeof lstLegend.min === 'number' ? lstLegend.min : 0;
            const max = typeof lstLegend.max === 'number' ? lstLegend.max : 50;
            return (
              <div className="space-y-1.5">
                <div className="relative w-full">
                  <div
                    className={lg.gradientBorder}
                    style={{ background: `linear-gradient(to right, ${lstLegend.colors.join(', ')})` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const value = min + ratio * (max - min);
                      return (
                        <div key={idx} className="flex flex-col items-center">
                          <div className="w-0.5 h-1.5 bg-white opacity-80" />
                          {idx % 2 === 0 && (
                            <span className="text-[8px] sm:text-[9px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] mt-0.5">
                              {value.toFixed(1)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={lg.rangeText}>
                  <span>{min.toFixed(1)} °C</span>
                  <span>{max.toFixed(1)} °C</span>
                </div>
                {lstLegend.description && (
                  <p className={lg.bodySm}>
                    {lstLegend.description}
                  </p>
                )}
              </div>
            );
          })()}
          {/* VHI Philippines: use gradient legend from API vis_params, similar to BSRI */}
          {activeWMSLayer.type === 'vhi' && vhiPHData && vhiPHData.tiles && layerManagerRef.current && (() => {
            const vhiLegend = layerManagerRef.current.getVHIPHLegendInfo() as { colors: string[]; labels: string[]; min?: number; max?: number; description?: string } | null;
            if (!vhiLegend || !vhiLegend.colors?.length) return null;
            const min = typeof vhiLegend.min === 'number' ? vhiLegend.min : 0;
            const max = typeof vhiLegend.max === 'number' ? vhiLegend.max : 1;
            return (
              <div className="space-y-1.5">
                <div className="relative w-full">
                  <div
                    className={lg.gradientBorder}
                    style={{ background: `linear-gradient(to right, ${vhiLegend.colors.join(', ')})` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const value = min + ratio * (max - min);
                      return (
                        <div key={idx} className="flex flex-col items-center">
                          <div className="w-0.5 h-1.5 bg-white opacity-80" />
                          {idx % 2 === 0 && (
                            <span className="text-[8px] sm:text-[9px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] mt-0.5">
                              {value.toFixed(2)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={lg.rangeText}>
                  <span>{min.toFixed(2)}</span>
                  <span>{max.toFixed(2)}</span>
                </div>
                {vhiLegend.description && (
                  <p className={lg.bodySm}>
                    {vhiLegend.description}
                  </p>
                )}
              </div>
            );
          })()}
          {activeWMSLayer.type === 'bsri' && (() => {
            const bsri = getCropAreaLegendInfo().bsri as { colors: string[]; labels: string[]; min?: number; max?: number } | undefined;
            if (!bsri || !bsri.colors?.length) return null;
            const min = typeof bsri.min === 'number' ? bsri.min : 0;
            const max = typeof bsri.max === 'number' ? bsri.max : 5;
            return (
              <div className="space-y-1.5">
                <div className="relative w-full">
                  <div
                    className={lg.gradientBorder}
                    style={{ background: `linear-gradient(to right, ${bsri.colors.join(', ')})` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const value = min + ratio * (max - min);
                      return (
                        <div key={idx} className="flex flex-col items-center">
                          <div className="w-0.5 h-1.5 bg-white opacity-80" />
                          {idx % 2 === 0 && (
                            <span className="text-[8px] sm:text-[9px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] mt-0.5">
                              {value.toFixed(1)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* RapidDamage baseline yield — compact map legend */}
      {floodDamageRasterActive && !isChangeDetectionMode && (
        <div
          className={`${lg.anchorCompact} ${lg.shellCompact}`}
        >
          <div className="flex items-center justify-between gap-1 mb-1">
            <h5 className={`${lg.titleSm} truncate leading-tight`}>{FLOOD_DAMAGE_RASTER_LEGEND.title}</h5>
            <button
              type="button"
              title="About baseline yield"
              aria-label="About baseline yield legend"
              aria-expanded={showFloodDamageLegendInfo}
              onClick={() => setShowFloodDamageLegendInfo(true)}
              className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
            >
              <i className="ri-information-line text-xs sm:text-sm" />
            </button>
          </div>
          <div className="space-y-0.5">
            <div
              className={`h-3.5 sm:h-4 w-full rounded-full overflow-hidden`}
              style={{ background: `linear-gradient(to right, ${FLOOD_DAMAGE_RASTER_LEGEND.colors.join(', ')})` }}
            />
            <div className={`${lg.rangeText} text-[7px]`}>
              <span>{FLOOD_DAMAGE_RASTER_LEGEND.labels[0]} ({FLOOD_DAMAGE_RASTER_LEGEND.min})</span>
              <span>{FLOOD_DAMAGE_RASTER_LEGEND.labels[1]} ({FLOOD_DAMAGE_RASTER_LEGEND.max})</span>
            </div>
          </div>
        </div>
      )}

      {/* Crop type maps — one layer at a time, drawn on the map */}
      {!isChangeDetectionMode && layerManagerRef.current && (() => {
        const activeCrop = (
          [
            ['maize', 'Maize'],
            ['rice', 'Rice'],
            ['sugarCane', 'Sugarcane'],
            ['coconut', 'Coconut'],
          ] as const
        ).find(([key]) => layerVisibility[key]);
        if (!activeCrop) return null;
        const cropLegend = layerManagerRef.current.getCropAreaLegendInfo(activeCrop[0]);
        if (!cropLegend?.colors?.length) return null;
        return (
          <div className={`${lg.anchorCompact} ${lg.shellCompact}`}>
            <h5 className={`${lg.titleSm} mb-1.5`}>{activeCrop[1]}</h5>
            <div className="space-y-1">
              {cropLegend.colors.map((color, index) => (
                <div key={cropLegend.labels[index] ?? index} className="flex items-center space-x-1.5">
                  <div className={lg.swatchBorder} style={{ backgroundColor: color }} />
                  <span className={lg.labelSm}>{cropLegend.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Rice planting method — on-map discrete legend (same place as crop type / CADI) */}
      {!isChangeDetectionMode &&
        (currentLayerVisibility.plantingMethodDry2025 ||
          currentLayerVisibility.plantingMethodDry2024) && (
          <div className={`${lg.anchorCompact} ${lg.shellCompact}`}>
            <h5 className={`${lg.titleSm} mb-1.5`}>
              Planting Method (
              {currentLayerVisibility.plantingMethodDry2025 ? 'Dry 2025' : 'Dry 2024'})
            </h5>
            <div className="space-y-1">
              {PLANTING_METHOD_LEGEND.colors.map((color, index) => (
                <div key={PLANTING_METHOD_LEGEND.labels[index]} className="flex items-center space-x-1.5">
                  <div className={lg.swatchBorder} style={{ backgroundColor: color }} />
                  <span className={lg.labelSm}>{PLANTING_METHOD_LEGEND.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Rice growth stage — on-map discrete legend */}
      {!isChangeDetectionMode && currentLayerVisibility.riceGrowthStage && (
        <div className={`${lg.anchorCompact} ${lg.shellCompact}`}>
          <h5 className={`${lg.titleSm} mb-1.5`}>Rice Growth Stage ({getRiceGrowthStagePeriod(riceGrowthStagePeriod).label})</h5>
          <div className="space-y-1">
            {RICE_GROWTH_STAGE_LEGEND.colors.map((color, index) => (
              <div key={RICE_GROWTH_STAGE_LEGEND.labels[index]} className="flex items-center space-x-1.5">
                <div className={lg.swatchBorder} style={{ backgroundColor: color }} />
                <span className={lg.labelSm}>{RICE_GROWTH_STAGE_LEGEND.labels[index]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Irrigation type — on-map discrete legend */}
      {!isChangeDetectionMode && currentLayerVisibility.irrigationType && (
        <div className={`${lg.anchorCompact} ${lg.shellCompact}`}>
          <h5 className={`${lg.titleSm} mb-1.5`}>Irrigation Type ({IRRIGATION_TYPE_LABEL})</h5>
          <div className="space-y-1">
            {IRRIGATION_TYPE_LEGEND.colors.map((color, index) => (
              <div key={IRRIGATION_TYPE_LEGEND.labels[index]} className="flex items-center space-x-1.5">
                <div className={lg.swatchBorder} style={{ backgroundColor: color }} />
                <span className={lg.labelSm}>{IRRIGATION_TYPE_LEGEND.labels[index]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CADI — discrete class legend, same layout as rice growth stage */}
      {activeWMSLayer?.type === 'cadi' && !isChangeDetectionMode && layerManagerRef.current && (() => {
        const cadiLegend = layerManagerRef.current.getCADIDroughtLegendInfo();
        if (!cadiLegend?.colors?.length) return null;
        const frame = CADI_DROUGHT_FRAMES[cadiCurrentIndex] ?? CADI_DROUGHT_FRAMES[0];
        return (
          <div className={`${lg.anchorCompact} ${lg.shellCompact}`}>
            <div className="flex items-start justify-between gap-1 mb-1.5">
              <h5 className={`${lg.titleSm} leading-tight`}>
                CADI Drought{frame?.label ? ` (${frame.label})` : ''}
              </h5>
              <button
                type="button"
                title="About CADI values"
                aria-label="About CADI legend values"
                aria-expanded={showCadiLegendInfo}
                onClick={() => setShowCadiLegendInfo(true)}
                className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
              >
                <i className="ri-information-line text-xs sm:text-sm" />
              </button>
            </div>
            <div className="space-y-1.5">
              {cadiLegend.colors.map((color, index) => (
                <div key={cadiLegend.labels[index] ?? index} className="flex items-center space-x-1.5">
                  <div className={lg.swatchBorder} style={{ backgroundColor: color }} />
                  <span className={lg.labelSm}>{cadiLegend.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* CADI / pest risk info popups — rendered at map root so text is readable */}
      <InfoPopupDialog
        open={showFloodDamageLegendInfo}
        onClose={() => setShowFloodDamageLegendInfo(false)}
        title="About baseline yield"
        accent="amber"
      >
        <p className="mb-3">{FLOOD_DAMAGE_RASTER_LEGEND.description}</p>
      </InfoPopupDialog>
      <InfoPopupDialog
        open={showCadiLegendInfo}
        onClose={() => setShowCadiLegendInfo(false)}
        title="About El Niño CADI"
        accent="amber"
      >
        <p className="mb-3">{CADI_DROUGHT_DESCRIPTION}</p>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <span className="font-semibold">Legend: </span>
          {CADI_DROUGHT_LEGEND_DESCRIPTION}
        </p>
      </InfoPopupDialog>
      <InfoPopupDialog
        open={showPestRiskLegendInfo}
        onClose={() => setShowPestRiskLegendInfo(false)}
        title="About Pest Occurrence Probability Index"
        accent="orange"
      >
        <p className="mb-3">{PEST_RISK_DESCRIPTION}</p>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <span className="font-semibold">Legend: </span>
          {PEST_RISK_LEGEND_DESCRIPTION}
        </p>
      </InfoPopupDialog>

      {/* Pest Occurrence Probability Index — compact map legend */}
      {activeWMSLayer?.type === 'pestRisk' && !isChangeDetectionMode && layerManagerRef.current && (() => {
        const pestLegend = layerManagerRef.current.getPestRiskLegendInfo();
        if (!pestLegend?.colors?.length) return null;
        const compactLabels = ['Low', 'Moderate', 'High'];
        return (
          <div
            className={`${lg.anchorCompact} ${lg.shellCompact}`}
          >
            <div className="flex items-center justify-between gap-1 mb-1">
              <h5 className={`${lg.titleSm} truncate leading-tight`} title="Pest Occurrence Probability Index">
                Pest Occurrence
              </h5>
              <button
                type="button"
                title="About Pest Occurrence Probability Index"
                aria-label="About Pest Occurrence Probability Index legend"
                aria-expanded={showPestRiskLegendInfo}
                onClick={() => setShowPestRiskLegendInfo(true)}
                className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
              >
                <i className="ri-information-line text-xs sm:text-sm" />
              </button>
            </div>
            <div className="space-y-0.5">
              <div className="relative w-full">
                <div
                  className={`h-3.5 sm:h-4 w-full rounded-full overflow-hidden`}
                  style={{ background: `linear-gradient(to right, ${pestLegend.colors.join(', ')})` }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-0.5 pointer-events-none">
                  {compactLabels.map((label) => (
                    <span
                      key={label}
                      className="text-[6px] sm:text-[7px] font-semibold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)] leading-none"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
              <div className={`${lg.rangeText} text-[7px]`}>
                <span>0</span>
                <span>1</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Rice Yield Forecast legend (Field / Large-scale) - same top-right position */}
      {currentLayerVisibility.riceForecast && !isChangeDetectionMode && (() => {
        const riceForecastLegend = getCropAreaLegendInfo().riceForecast as { colors: string[]; min?: number; max?: number; description?: string; scope?: string } | undefined;
        if (!riceForecastLegend || !riceForecastLegend.colors?.length) return null;
        const min = typeof riceForecastLegend.min === 'number' ? riceForecastLegend.min : 0;
        const max = typeof riceForecastLegend.max === 'number' ? riceForecastLegend.max : 7.5;
        const isFarmScope = riceForecastLegend.scope === 'farm';
        return (
          <div
            className={`${activeWMSLayer ? lg.anchorStacked : lg.anchor} ${lg.shell}`}
          >
            <h5 className={`${lg.title} mb-2`}>
              {isFarmScope ? 'Rice Yield Forecast (Field, t/ha)' : 'Rice Yield Forecast (t/ha)'}
            </h5>
            <div className="space-y-1.5">
              <div className="relative w-full">
                <div
                  className={lg.gradientBorder}
                  style={{ background: `linear-gradient(to right, ${riceForecastLegend.colors.join(', ')})` }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
                  {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                    const value = min + ratio * (max - min);
                    return (
                      <div key={idx} className="flex flex-col items-center">
                        <div className="w-0.5 h-1.5 bg-white opacity-80" />
                        {idx % 2 === 0 && (
                          <span className="text-[8px] sm:text-[9px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] mt-0.5">
                            {value.toFixed(1)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={lg.rangeText}>
                <span>{min.toFixed(1)} t/ha</span>
                <span>{max.toFixed(1)} t/ha</span>
              </div>
              {riceForecastLegend.description && (
                <p className={lg.bodySm}>
                  {riceForecastLegend.description}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Status Indicators + Inset Overview Map */}
      <MapStatusIndicators
        analysisInfo={analysisInfo}
        zoomLevel={zoomLevel}
        mapCoordinates={mapCoordinates}
        selectedField={selectedField}
        isLoadingGeoJson={isLoadingGeoJson}
        mainMap={mapInstance ?? undefined}
      />

    </div>
  );}
